/**
 * 화면 설명 (사용자 가이드)
 * ---------------------------------------------------------------------------
 * 화면마다 "여기가 무엇이고, 여기서 한 일이 어디에 영향을 주는지" 를 적는다.
 *
 * 왜 한곳에 모았나
 *   화면마다 설명을 심어 두면 같은 말이 흩어지고, 어떤 화면에는 있고 어떤
 *   화면에는 없게 된다. 여기 한 표에 모으면 빠진 화면이 바로 보인다.
 *   레이아웃이 현재 주소로 이 표를 찾아 오른쪽 위에 같은 모양으로 띄운다.
 *
 * 무엇을 쓰나
 *   "이 버튼은 저장합니다" 같은 말은 쓰지 않는다. 보면 안다.
 *   **다른 화면과의 연결**을 쓴다 — 여기서 만든 역할이 어디서 쓰이는지,
 *   요금제를 바꾸면 무엇이 달라지는지. 그건 화면만 봐서는 알 수 없다.
 */

import type { ReactNode } from 'react';
import { HelpH, HelpList, HelpP, HelpPath } from '../components/PageHelp';
import { AiQuotaExample } from '../features/tenant/AiWeeklyReportModal';

export interface GuideEntry {
  title: string;
  body: ReactNode;
}

/** 메뉴 키 → 설명. 키는 lib/menu.tsx 의 것과 같다. */
export const GUIDE: Record<string, GuideEntry> = {
  /* ---- 기업 관리자 ---- */

  /*
   * 설명은 **경로 이름**으로 찾는다(guideKeyFromPath). 그래서 키가
   * 라우트와 같아야 한다 — `/app/dashboard` 는 기업의 「자사 현황」이고,
   * `/app/overview` 는 시스템 관리자의 「운영 현황」이다. 한때 이 둘이
   * 뒤바뀌어 있어, 고객이 자사 현황에서 설명을 누르면 「연체 금액」·「한도에
   * 막힌 기업」 같은 **운영자용 문구**가 떴다.
   */
  dashboard: {
    title: '자사 현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          우리 기업의 상태를 한눈에 봅니다. 구성원 수, 쓰고 있는 제품, 이번 달 사용량이
          모입니다.
        </HelpP>

        <HelpH>AI 주간 보고서</HelpH>
        <HelpP>
          오른쪽 위 단추를 누르면 고른 기간의 <b>구성원 변동·접속·로그인 실패·사용량·
          청구·활동 기록</b>을 AI 가 읽고 한 장으로 정리해 줍니다. 화면의 숫자는 「지금
          어떤가」를 말해 주지만, <b>지난주와 무엇이 달라졌는지</b>는 사람이 머리로
          비교해야 했습니다. 그 비교를 대신합니다.
        </HelpP>
        <HelpList
          items={[
            { term: '기간', desc: '한 번에 최대 1개월. 기본값은 지난 7일입니다.' },
            {
              term: '무료 수량',
              desc: (
                <>
                  창 위에 <AiQuotaExample /> 가 떠서 이번 달에 몇 건을 만들었는지 보여
                  줍니다. 괄호 안의 무료 수량은 지금 계약하신 요금제에서 읽어 온 값이라
                  기업마다 다릅니다. 무료 수량은 기업 단위(사람 수와 무관)이고 매월 1일에
                  새로 시작하며, 쓰지 않은 수량은 다음 달로 넘어가지 않습니다.
                </>
              ),
            },
            {
              term: '요금',
              desc: '무료 수량을 넘긴 건부터 건당 비용이 발생해 다음 청구서에 더해집니다. 그 단가는 계약하신 요금제의 금액이고, 창 안내에 함께 적힙니다. 만들다 실패하면 청구하지 않습니다.',
            },
            {
              term: '동의',
              desc: '만들 때마다 「선택된 모델에 자료를 제공하는 것에 동의합니다」 체크를 받습니다. 체크하지 않으면 단추가 눌리지 않고 서버도 거절합니다. 동의한 사실과 그때의 문구는 보고서마다 남아, 지난 보고서 탭에서 확인하실 수 있습니다.',
            },
            {
              term: '개인정보',
              desc: '이름·이메일은 AI 에 보내지 않습니다. 숫자와 분류만 보냅니다 — 누가 무엇을 했는지는 로그 조회에서 봅니다.',
            },
          ]}
        />
        <HelpP>
          만드신 보고서는 같은 팝업의 <b>「지난 보고서」</b> 탭에 모두 남습니다.{' '}
          <b>다시 보는 데는 요금이 들지 않습니다</b> — 새로 만들 때만 붙습니다. AI 가
          쓰는 글이라 같은 기간으로 눌러도 표현이 달라지므로, 보고에 쓰신 그 글을
          그대로 다시 꺼내실 수 있게 남겨 둡니다. 목록의 <b>건당 요금</b>과{' '}
          <b>만든 날짜</b>가 청구서의 「AI 보고서 생성」 줄과 맞습니다.
        </HelpP>

        <HelpH>메뉴를 못 찾겠다면</HelpH>
        <HelpP>
          왼쪽 위 <HelpPath>메뉴 검색</HelpPath>(또는 <b>Ctrl+K</b>)에 이름 일부를 적으면
          바로 갑니다. 권한이 없는 메뉴는 검색에도 나오지 않습니다.
        </HelpP>

        <HelpH>처음이라면 이 순서로</HelpH>
        <HelpList
          items={[
            {
              term: '1. 구성원 초대',
              desc: (
                <>
                  <HelpPath>내 기업 → 구성원</HelpPath> 에서 이메일로 초대합니다. 초대받은
                  사람은 메일의 링크로 비밀번호를 정하고 들어옵니다.
                </>
              ),
            },
            {
              term: '2. 역할 주기',
              desc: (
                <>
                  <HelpPath>역할·권한</HelpPath> 에서 누가 무엇을 할 수 있는지 정합니다.
                  역할을 주지 않으면 로그인은 되지만 보이는 화면이 없습니다.
                </>
              ),
            },
            {
              term: '3. 제품 신청',
              desc: (
                <>
                  <HelpPath>제품</HelpPath> 에서 WORK&ECO 같은 제품을 신청합니다. 신청해야
                  그 제품에 들어갈 수 있습니다.
                </>
              ),
            },
          ]}
        />
      </>
    ),
  },

  users: {
    title: '구성원 — 화면 설명',
    body: (
      <>
        <HelpP>
          우리 기업에서 이 서비스를 쓸 사람을 관리합니다. 여기에 없는 사람은 로그인할 수
          없습니다.
        </HelpP>

        <HelpH>청구 문서 수신 — 청구서·세금계산서를 받을 사람</HelpH>
        <HelpP>
          표의 <b>「청구 문서 수신」</b> 을 켜면 그 구성원이{' '}
          <b>청구서 안내 메일</b>을 받고, <b>세금계산서</b>도 그 주소로 발급됩니다.
          받아야 하는 사람은 보통 경리 담당자인데 Hub 관리자가 아닌 경우가 흔해서
          따로 고르게 했습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '아무도 안 고르면',
              desc: '소유자·관리자가 모두 받습니다. 스위치가 전부 꺼져 있다고 해서 아무도 못 받는 것이 아닙니다 — 표 위 안내에 지금 받는 분의 이름이 적혀 있습니다.',
            },
            {
              term: '한 명이라도 켜면',
              desc: '그 사람들만 받습니다. 소유자·관리자라도 켜지 않으면 받지 않으니, 대표님께 매달 청구서가 가는 것을 멈추고 싶을 때 쓰시면 됩니다.',
            },
            {
              term: '세금계산서는 앞의 두 분까지',
              desc: '국세청 양식이 공급받는자 이메일을 두 칸만 받습니다. 먼저 켠 분이 이메일1 입니다. 세 번째부터는 청구서 안내 메일만 받습니다.',
            },
            {
              term: '퇴사·정지되면',
              desc: '자동으로 빠집니다. 지정한 분이 모두 빠지면 다시 소유자·관리자가 받습니다 — 퇴사 한 번으로 청구서가 사라지지 않습니다.',
            },
            {
              term: '누가 바꿀 수 있나',
              desc: '과금 변경 권한이 있는 분만입니다. 구성원을 고칠 수 있다고 해서 청구서 수신처까지 바꿀 수 있으면 안 되기 때문입니다.',
            },
          ]}
        />

        <HelpH>여러 명을 한 번에</HelpH>
        <HelpP>
          <HelpPath>엑셀 일괄등록</HelpPath> 으로 수십 명을 한 번에 넣을 수 있습니다. 먼저
          서식을 내려받으세요 — 그 파일의 「코드표」 시트에 <b>이 회사에 등록된 부서·직책
          이름</b>이 들어 있고, 그 이름을 그대로 적어야 올라갑니다. 파일을 올리면 바로
          등록되지 않고, 어느 줄이 틀렸는지와 계약 인원이 얼마나 늘어나는지를 먼저
          보여 줍니다. 개인정보를 수집할 근거가 있다는 확인에 동의해야 등록되며, 그
          동의는 기록으로 남습니다.
        </HelpP>
        <HelpH>초대 흐름</HelpH>
        <HelpList
          items={[
            { term: '초대', desc: '이메일을 넣으면 가입 링크가 메일로 갑니다.' },
            {
              term: '초대됨',
              desc: '아직 비밀번호를 정하지 않은 상태입니다. 메일이 안 갔다면 다시 보낼 수 있습니다.',
            },
            { term: '활성', desc: '정상적으로 쓰는 사람입니다.' },
            {
              term: '정지',
              desc: '로그인을 막습니다. 기록은 남으므로 그만둔 사람은 삭제가 아니라 정지로 둡니다.',
            },
            {
              term: '퇴사',
              desc: (
                <>
                  퇴사일을 넣으면 <b>계정도 함께 잠깁니다</b> — 그 사람은 더 이상 로그인할 수
                  없습니다. 다시 열어 주려면 <HelpPath>로그인 중지</HelpPath> 를 풀면 됩니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>다른 화면과의 연결</HelpH>
        <HelpList
          items={[
            {
              term: '역할·권한',
              desc: '여기서 초대한 사람에게 역할을 줘야 화면이 보입니다. 역할이 없으면 빈 화면이 됩니다.',
            },
            { term: '조직도', desc: '부서를 만들어 두면 구성원을 부서에 넣을 수 있습니다.' },
            {
              term: '사용량·청구',
              desc: '요금제에 계약해 둔 인원까지 쓸 수 있습니다.',
            },
          ]}
        />
        <HelpH>계약 인원을 넘겨 초대하면</HelpH>
        <HelpP>
          막지 않습니다. 대신 <b>늘어나는 월 요금을 알려 주고 진행할지 묻습니다.</b>
          「예」를 고르면 계약 인원이 그만큼 올라가고, 누가 언제 왜 늘렸는지가 기업 정보에
          기록으로 남습니다. 요금제의 최대 인원까지만 늘릴 수 있고, 그 위로 가려면 상위
          요금제로 바꿔야 합니다.
        </HelpP>

        <HelpH>무료 체험이 끝나고 결제하지 않으면</HelpH>
        <HelpP>
          조회는 되지만 <b>등록·수정과 내려받기가 막힙니다.</b> 결제가 확인되면 바로
          풀립니다.
        </HelpP>
        <HelpH>여기 적은 인사 정보가 제품에서 쓰이는 곳</HelpH>
        <HelpList
          items={[
            {
              term: '입사일',
              desc: 'WORK&CRM 이 근속연수를 세어 연차 부여일수를 정합니다. 비워 두면 그 사람의 연차가 계산되지 않습니다.',
            },
            {
              term: '부서 · 직위',
              desc: 'WORK&CRM 의 조직도·결재선과 WORK&ECO 의 확인자 목록에 그대로 나옵니다.',
            },
            {
              term: 'CRM 권한',
              desc: '대표이사·부서장·팀장·팀원 중에서 고릅니다. 고른 값이 WORK&CRM 에서 쓸 수 있는 메뉴를 정합니다.',
            },
          ]}
        />
        <HelpH>개인정보</HelpH>
        <HelpP>
          이름·연락처·생년월일·입사일은 암호화해서 보관합니다. 목록에서 누가 무엇을 열어
          봤는지는 <HelpPath>로그 조회</HelpPath> 에 남습니다.
        </HelpP>
      </>
    ),
  },

  departments: {
    title: '조직도 — 화면 설명',
    body: (
      <>
        <HelpP>
          부서를 나무 구조로 만듭니다. 상위 부서 아래에 하위 부서를 두는 식입니다.
        </HelpP>
        <HelpH>어디에 쓰이나</HelpH>
        <HelpList
          items={[
            { term: '구성원', desc: '사람을 부서에 넣어 "누가 어느 부서인지" 를 관리합니다.' },
            {
              term: '제품',
              desc: 'WORK&ECO 같은 제품에서 부서별로 자료를 나눠 보는 데 쓰입니다.',
            },
          ]}
        />
        <HelpH>만들 때의 규칙</HelpH>
        <HelpList
          items={[
            {
              term: '부서 코드',
              desc: '회사 안에서 겹칠 수 없습니다. 이미 쓰는 코드를 넣으면 저장되지 않습니다.',
            },
            { term: '단계', desc: '상위 아래 하위로 최대 10단계까지 둘 수 있습니다.' },
            {
              term: '부서장',
              desc: '우리 회사 구성원 중에서만 고를 수 있습니다. 그만둔 사람은 고를 수 없습니다.',
            },
          ]}
        />
        <HelpH>지울 수 없다고 나오면</HelpH>
        <HelpP>
          두 가지 중 하나입니다. <b>하위 부서가 남아 있거나</b>, 그 부서에 속한 구성원이
          있는 경우입니다. 하위 부서를 먼저 정리하고, 사람은 다른 부서로 옮긴 뒤 지우세요.
          부서를 당장 없애기 어렵다면 <HelpPath>사용 안 함</HelpPath> 으로 돌려 두어도
          됩니다 — 목록에서는 빠지지만 지난 기록은 그대로 남습니다.
        </HelpP>
      </>
    ),
  },

  roles: {
    title: '역할·권한 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>누가 무엇을 할 수 있는지</b>를 정하는 화면입니다. 사람마다 권한을 일일이 주는
          대신, 역할을 만들어 두고 사람에게 역할을 줍니다.
        </HelpP>
        <HelpH>역할과 권한</HelpH>
        <HelpList
          items={[
            { term: '역할', desc: '예: 기업 관리자, 일지 작성자, 조회 전용' },
            { term: '권한', desc: '예: 사업장 관리, 일지 작성, 로그 조회' },
            { term: '관계', desc: '역할 하나에 권한 여러 개를 담고, 사람에게 역할을 줍니다.' },
          ]}
        />
        <HelpH>권한을 고치려면 — 「권한」 탭에서 바로</HelpH>
        <HelpP>
          역할을 고른 뒤 <b>「권한」 탭 → [권한 고치기]</b> 를 누르면, 보고 계신 그 자리에서
          바로 켜고 끌 수 있습니다. 고친 뒤 <b>[저장]</b> 을 누르셔야 반영되며,{' '}
          <b>[되돌리기]</b> 로 언제든 취소하실 수 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '우리 회사 역할이면',
              desc: '그 역할이 바로 고쳐집니다. 저장하면 그 역할을 가진 모든 분의 권한이 함께 바뀌며, 몇 명인지 저장 줄에 적혀 있습니다.',
            },
            {
              term: '시스템 역할이면',
              desc: '원본은 그대로 두고, 켜 두신 내용으로 「우리 회사 전용 역할」이 새로 만들어집니다. 이름은 저장 줄에서 바꾸실 수 있고, 만든 뒤에는 「구성원」 탭에서 사람을 더해 주시면 됩니다.',
            },
          ]}
        />
        <HelpP>
          <b>왜 시스템 역할은 복사본이 되나.</b> 기업 소유자·일반 사용자처럼 미리 들어 있는
          역할은 <b>모든 고객사가 함께 쓰는 정의</b>입니다. 한 칸을 끄면 우리 회사만이
          아니라 전체에 영향이 갑니다. 그래서 원본은 지키고 복사본을 만들어 드립니다 —
          누르시는 입장에서는 <b>켜고 저장, 한 번</b>으로 끝납니다.
        </HelpP>
        <HelpP>
          예를 들어 팀원에게 프로젝트의 통합첨부파일과 진척현황만 더 열어 주고 싶으시면 —
          「일반 사용자」의 권한 탭에서 그 두 칸을 더 켜고 저장하시면{' '}
          <b>「일반 사용자 (우리 회사)」</b> 역할이 만들어집니다. 그 사람에게 그 역할을
          주시면 됩니다. 한 사람이 역할 여러 개를 가질 수 있고 <b>권한은 합쳐지므로</b>,
          기존 역할을 그대로 두고 새 역할을 더해 주셔도 됩니다.
        </HelpP>

        <HelpH>차단이 허용보다 셉니다</HelpH>
        <HelpP>
          사람에게 권한을 따로 더 주거나 뺄 수도 있습니다. 같은 권한에 허용과 차단이 겹치면{' '}
          <b>차단이 이깁니다.</b> 사고를 막는 쪽이 우선이기 때문입니다.
        </HelpP>
        <HelpH>제품 화면까지 함께 여는 역할</HelpH>
        <HelpList
          items={[
            {
              term: '인사담당자',
              desc: (
                <>
                  이 역할을 주면 <b>WORK&CRM 의 인사관리 메뉴가 통째로 열립니다</b> — 직원정보·
                  조직도·연차정책·연차사용현황까지. CRM 에서 메뉴를 하나씩 체크할 필요가
                  없습니다. 직급과는 다른 축이라 팀원이면서 인사담당자일 수 있습니다.
                  여기서 역할을 거두면 CRM 쪽도 곧 닫힙니다.
                </>
              ),
            },
            {
              term: 'ECO 환경책임자 · 일지작성자',
              desc: (
                <>
                  WORK&ECO 에서 사업장을 등록할 수 있는지, 일지를 쓰기만 하는지를 정합니다.
                  <b> 이 역할을 주면 그 사람이 WORK&ECO 사용자 한 명으로 계산됩니다</b> —
                  요금이 늘어납니다. 역할을 거두면 다음 달부터 빠집니다. 기업 관리자는
                  역할과 무관하게 모든 제품에 들어갈 수 있어 항상 한 자리를 차지하므로,
                  ECO 는 최소 3명부터 계약됩니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>역할을 만들 때</HelpH>
        <HelpList
          items={[
            {
              term: '역할 코드',
              desc: '영문 대문자·숫자·밑줄만 씁니다(예: SAFETY_LEAD). 회사 안에서 겹칠 수 없습니다.',
            },
            {
              term: '시스템 역할',
              desc: '기업 소유자·관리자·구성원처럼 미리 들어 있는 역할입니다. 모든 고객사가 함께 쓰는 정의라 고치거나 지울 수 없고, 같은 이름으로 새 역할을 만들 수도 없습니다. 「권한」 탭의 [복사해서 만들기] 로 우리 회사 전용 역할을 만들어 쓰세요.',
            },
            {
              term: '고를 수 있는 권한',
              desc: '우리 회사에서 쓸 수 있는 것만 목록에 나옵니다. 모든 고객사를 다루는 운영자 전용 권한은 아예 나오지 않습니다.',
            },
            {
              term: '아직 구독하지 않은 제품의 권한',
              desc: '목록에는 보이지만 회색으로 표시됩니다. 담아 두어도 그 제품을 신청해야 실제로 열립니다.',
            },
          ]}
        />
        <HelpH>지울 수 없다고 나오면</HelpH>
        <HelpP>
          그 역할을 <b>쓰고 있는 사람이 남아 있는 것</b>입니다. 구성원 화면에서 역할을
          먼저 바꿔 준 뒤 지우세요.
        </HelpP>
        <HelpH>권한이 없는 메뉴는 보이지 않습니다</HelpH>
        <HelpP>
          좌측 메뉴는 그 사람이 실제로 열 수 있는 화면만 보여 줍니다. 역할을 줬는데도
          상대방에게 메뉴가 안 보인다면 그 역할에 해당 권한이 담겨 있는지 확인하세요.
        </HelpP>
        <HelpH>바꾸면 언제 반영되나</HelpH>
        <HelpP>
          권한은 성능을 위해 잠시 기억해 둡니다. 바꾼 뒤 최대 5분이 지나면 반영되고, 그
          사람이 다시 로그인하면 즉시 반영됩니다.
        </HelpP>
      </>
    ),
  },

  store: {
    title: '제품 — 화면 설명',
    body: (
      <>
        <HelpP>
          쓰고 있는 제품과 더 쓸 수 있는 제품을 봅니다. 매일 들어오는 이유는 신청이 아니라{' '}
          <b>바로가기</b>입니다.
        </HelpP>
        <HelpH>신청하면 어떻게 되나</HelpH>
        <HelpList
          items={[
            { term: '무료 체험', desc: '정해진 기간 동안 결제 없이 씁니다.' },
            {
              term: '체험 끝나기 전',
              desc: '만료 7일·3일·1일 전에 메일로 알려 드립니다.',
            },
            {
              term: '체험이 끝나면',
              desc: '조회는 되지만 새로 등록하거나 고칠 수 없게 됩니다. 제품 화면 위에 그 사실이 표시됩니다.',
            },
          ]}
        />
        <HelpH>그만 쓰려면</HelpH>
        <HelpP>
          제품마다 <HelpPath>해지</HelpPath> 가 있습니다. 바로 끊기지 않고{' '}
          <b>이번 기간이 끝나는 날까지 쓰고 멈춥니다.</b> 그 전에 마음이 바뀌면{' '}
          <HelpPath>재개</HelpPath> 로 되돌릴 수 있고, 되돌리면 끊김 없이 이어집니다.
          제품 하나가 아니라 서비스 전체를 끝내려면 <HelpPath>기업 설정</HelpPath> 아래쪽의
          서비스 종료를 쓰세요.
        </HelpP>
        <HelpH>신청·해지는 누가 하나</HelpH>
        <HelpP>
          <b>소유자와 관리자만</b> 할 수 있습니다. 회사 비용이 달라지는 일이기 때문입니다.
          일반 구성원에게는 이 메뉴가 보이지 않습니다.
        </HelpP>
        <HelpH>바로가기가 잠겨 있다면</HelpH>
        <HelpP>
          그 제품의 접속 주소가 아직 등록되지 않은 것입니다. 플랫폼 운영자에게 알려
          주세요.
        </HelpP>
      </>
    ),
  },

  billing: {
    title: '사용량·청구 — 화면 설명',
    body: (
      <>
        <HelpP>
          이번 달에 얼마나 썼고 얼마가 청구될지 봅니다. 사용량은 매일 집계됩니다.
        </HelpP>

        <HelpH>청구 금액</HelpH>
        <HelpP>
          <b>기본료 + 사용자(사업장) 수 × 단가 + 초과 사용료 − 할인</b>, 마지막에 부가세
          10%입니다. 사용자 수는 <b>계약한 수</b>로 셉니다 — 그달에 몇 명이 실제로
          접속했는지와는 다릅니다.
        </HelpP>

        <HelpH>약정 할인 (「약정 할인」 탭)</HelpH>
        <HelpP>
          정해진 기간을 쓰기로 하면 그 제품 청구액을 깎아 드립니다(기본{' '}
          <b>12개월 · 20%</b>). 조건은 시스템 관리자 Hub 의{' '}
          <HelpPath>시스템 → 공통코드 → BILLING_POLICY</HelpPath> 에서 정합니다.
        </HelpP>
        <HelpP>
          할인은 <b>그 제품 청구액 전체</b>에 걸립니다 — 정기 요금과 사람 수뿐 아니라
          저장 용량·AI 보고서 같은 <b>초과 사용분까지</b> 함께 깎입니다. 사람 수는 약정
          중에도 자유롭게 늘리고 줄이실 수 있고, 그달 청구액이 바뀌면 깎이는 금액도
          따라 바뀝니다.
        </HelpP>
        <HelpP>
          <b>중도에 그만두면 받은 할인을 되돌려 받습니다.</b> 위약금을 따로 매기지
          않습니다 — 약정하지 않았던 것과 같아질 뿐입니다. 금액은 지어내지 않고{' '}
          <b>실제로 발행된 청구서의 할인 줄을 더해</b> 셉니다. 그래서 고객이 물으면
          청구서를 짚어 가며 답할 수 있습니다. 화면에는 「지금 그만두시면 N원 반환」이
          늘 떠 있고, 서비스 종료 화면의 합계에도 같은 금액이 미리 들어갑니다.
        </HelpP>
        <HelpP>
          약정 기간을 <b>채우면 반환할 금액이 없습니다.</b> 기간이 끝나면 할인도 함께
          끝나므로, 계속 쓰실 것이면 그때 다시 약정하시면 됩니다. 약정을 중간에 푸는
          길은 서비스 종료뿐입니다 — 아무 때나 풀 수 있게 두면 「할인만 받고 바로 푸는」
          것이 되기 때문입니다.
        </HelpP>

        <HelpH>청구서 탭 읽는 법</HelpH>
        <HelpList
          items={[
            {
              term: '결제 수단',
              desc: '실제로 돈이 들어온 수단입니다 — 계좌이체 · 카드. 아직 안 내신 청구서는 비어 있습니다(—). 나눠 내셨으면 둘 다 보일 수 있습니다. 카드 결제가 거절된 기록은 세지 않습니다.',
            },
            {
              term: '세금계산서',
              desc: '국세청 승인번호와 발급일입니다. 「발급 예정」이면 아직 발급 전입니다 — 보통 청구서 발행 뒤 며칠 안에 나갑니다. 번호는 눌러서 복사하실 수 있습니다.',
            },
            {
              term: '메일로 받은 링크',
              desc: '청구서 안내 메일의 링크를 누르면 로그인하지 않고도 내역을 보실 수 있습니다. 여는 화면에서 그 기업의 사업자등록번호를 입력하셔야 열립니다. 경리 담당자처럼 Hub 계정이 없는 분께 그대로 전달하셔도 됩니다.',
            },
            {
              term: '누가 메일을 받나',
              desc: '기본은 소유자·관리자입니다. 경리 담당자처럼 따로 받을 분이 있으면 「구성원」 화면에서 그분의 「청구 문서 수신」 을 켜 주세요 — 켠 분들만 받고, 세금계산서도 그 주소로 발급됩니다.',
            },
          ]}
        />

        <HelpH>위쪽 카드 읽는 법</HelpH>
        <HelpList
          items={[
            {
              term: '제품 이름이 붙은 카드',
              desc: '「DB 사용량 · WORK&CRM」처럼 뒤에 제품이 붙으면 그 제품 몫만 센 것입니다. 제품마다 따로 세므로 같은 이름의 카드가 둘일 수 있습니다 — 중복이 아닙니다.',
            },
            {
              term: '「사용한 만큼」 딱지',
              desc: 'AI 보고서처럼 기본 제공량 없이 쓴 만큼만 내는 항목입니다. 한도 막대가 없고, 안 쓰면 0원입니다. 몇 건을 썼는지만 보여 줍니다.',
            },
            {
              term: '「초과」 딱지',
              desc: '한도를 넘긴 항목입니다. 초과가 허용된 항목이면 기능은 그대로 쓰면서 넘은 만큼 청구되고, 막아 둔 항목이면 그 자리에서 멈춥니다.',
            },
          ]}
        />

        <HelpH>한도를 넘으면</HelpH>
        <HelpP>
          요금제마다 사용자 수·저장 용량·트래픽 한도가 있고, 한도는{' '}
          <b>계약 수량에 따라 함께 늘어납니다</b>(예: 기본 20GB + 사용자당 5GB → 10명이면
          70GB). 제품을 둘 쓰면 <b>두 요금제의 한도를 더합니다</b> — CRM 15명 + ECO 3명을
          계약했다면 사용자 한도는 18명입니다. 산 만큼 쓸 수 있어야 하기 때문입니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '초과를 막아 둔 항목',
              desc: '한도에 닿는 순간 업로드·초대가 막힙니다. 추가 청구는 없습니다.',
            },
            {
              term: '초과가 허용된 항목',
              desc: '계속 쓸 수 있고, 넘은 만큼 다음 청구서에 더해집니다. 용량은 1GB 단위로 올려서 세므로 0.2GB 를 넘겨 써도 1GB 로 청구됩니다.',
            },
          ]}
        />
        <HelpP>
          제품을 여럿 쓰더라도 <b>같은 항목의 초과료는 한 번만</b> 청구됩니다. 저장 용량은
          기업 단위로 하나이기 때문입니다. 단가가 요금제마다 다르면 <b>싼 쪽</b>으로
          계산합니다. 미리 알림을 받으려면 담당자 이메일을{' '}
          <HelpPath>기업 설정</HelpPath> 에 넣어 두세요.
        </HelpP>

        <HelpH>달 중간에 시작하거나 끝나면</HelpH>
        <HelpP>
          기본료와 사용자 요금은 <b>쓴 날짜만큼만</b> 청구합니다. 10월 25일에 시작하면
          그달은 7일치(55,000원 → <b>12,419원</b>)만 나옵니다. 해지도 마찬가지로 끊은
          날까지만 받습니다. 청구서 적요에 몇 일치인지 적혀 있습니다.
        </HelpP>
        <HelpP>
          저장 용량·트래픽 <b>초과료는 날짜로 나누지 않습니다.</b> 이미 실제로 쓴 양이기
          때문입니다.
        </HelpP>

        <HelpH>무료 체험 중이라면</HelpH>
        <HelpP>
          체험이 그달 끝까지 이어지면 청구서가 나오지 않습니다. 체험이 달 중간에 끝나면
          <b> 끝난 다음 날부터</b> 계산됩니다.
        </HelpP>

        <HelpH>결제 수단 — 지금은 계좌이체입니다</HelpH>
        <HelpP>
          <HelpPath>결제 수단</HelpPath> 탭의 <b>[결제 수단 등록]</b> 을 누르면
          <b> 계좌이체</b>와 <b>카드 자동결제</b> 중에서 고르는 창이 열립니다.{' '}
          <b>기본은 계좌이체</b>이고, 카드는 결제 대행사 연동이 끝나기 전까지
          <b> 잠겨 있습니다</b> — 고장이 아니라 아직 준비되지 않은 것입니다. 준비되면
          따로 안내해 드립니다.
        </HelpP>
        <HelpP>
          창에는 고르신 수단에 따라 <b>언제 청구서가 나가고, 언제까지 내야 하며,
          계산서는 언제 발행되는지</b>가 함께 표시됩니다. 이 날짜들은 화면에 적어 둔
          것이 아니라 <b>실제 설정값을 읽어 온 것</b>이라, 정책이 바뀌면 안내도 같이
          바뀝니다.
        </HelpP>
        <HelpH>입금자명을 적어 주세요</HelpH>
        <HelpP>
          계좌이체를 고르시면 <b>입금자명</b> 칸이 함께 나옵니다. 통장에{' '}
          <b>찍히는 이름</b>을 적어 주시면 됩니다 — 상호와 같으면 비워 두셔도 됩니다.
        </HelpP>
        <HelpP>
          상호와 다른 이름으로 보내시는데 적어 두지 않으면, 저희는 입금 내역을 보고도
          어느 기업이 보내신 돈인지 알 수 없습니다. 그러면{' '}
          <b>납부하셨는데도 미납 안내가 나가고</b>, 심하면 서비스가 멈춥니다. 대표자
          개인 이름이나 경리 담당자 이름으로 보내시는 경우가 특히 그렇습니다.
        </HelpP>
        <HelpP>
          등록을 마치시면 결제 수단 카드에 <b>「등록됨」</b>과 적어 주신 입금자명이
          표시됩니다. 그 표시가 보이면 등록이 끝난 것입니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '계좌이체를 고르면',
              desc: '청구서가 나가면 적힌 계좌로 기한 안에 보내 주세요. 담당자가 입금을 확인해 수납으로 기록하면 청구서 탭의 상태가 「납부 완료」로 바뀌고 결제 수단 칸에 「계좌이체」가 뜹니다. 세금계산서는 사용월의 다음 달 초에 발행해 드립니다.',
            },
            {
              term: '카드를 고르면 (준비 중)',
              desc: '카드를 등록해 두시면 납기일에 자동으로 결제됩니다. 한도 초과나 유효기간 만료로 거절되면 사유를 담은 메일이 가고 다시 시도합니다. 카드로 내시면 카드매출전표가 증빙이 되므로 세금계산서는 따로 발행하지 않습니다.',
            },
            {
              term: '카드번호는 우리가 받지 않습니다',
              desc: '결제 대행사가 직접 받고, 우리 서버에는 대행사가 발급한 키만 암호화해 보관합니다.',
            },
          ]}
        />
        <HelpH>입금 계좌와 공급자 정보가 같은 화면에 있습니다</HelpH>
        <HelpP>
          <HelpPath>결제 수단</HelpPath> 탭 위쪽에 <b>어디로 넣는지</b>(은행·계좌번호·
          예금주)와 <b>계산서를 누구 앞으로 끊는지</b>(상호·사업자등록번호·대표자·업태·
          종목·주소)가 함께 떠 있습니다. 계좌번호는 <b>[복사]</b> 로 가져가실 수 있습니다 —
          눈으로 읽어 옮겨 적다 한 자리를 틀리면 <b>돈이 엉뚱한 곳으로 갑니다.</b>
        </HelpP>
        <HelpP>
          같은 자리에서 <b>사업자등록증</b>과 <b>통장 사본</b>을 내려받으실 수 있습니다.
          경리 담당자께 전달하실 때 쓰시면 됩니다 — 따로 요청하지 않으셔도 됩니다.
        </HelpP>
        <HelpH>탭에 붙는 「등록 필요」 딱지</HelpH>
        <HelpP>
          결제 수단을 아직 고르지 않으셨으면 탭 이름 옆에 <b>붉은 「등록 필요」</b>가
          붙습니다. 무료 기간이 끝나면 등록·수정이 막히고, 그때 가장 먼저 오셔야 하는
          곳이 여기입니다. 한 번 고르고 저장하시면 <b>초록 「등록됨」</b>으로 바뀝니다.
        </HelpP>
        <HelpP>
          계산서를 받을 메일 주소는 <HelpPath>구성원</HelpPath> 화면의{' '}
          <b>「청구 문서 수신」</b>에서 정합니다. 아무도 고르지 않으면 소유자·관리자가
          받습니다.
        </HelpP>

        <HelpH>누가 볼 수 있고 누가 바꿀 수 있나</HelpH>
        <HelpP>
          <b>금액은 소유자와 관리자만</b> 봅니다. 일반 구성원에게는 이 메뉴 자체가
          보이지 않습니다 — 회사가 얼마를 내는지는 아무나 볼 일이 아닙니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '조회',
              desc: '구독·청구서·예상 금액·결제수단 목록을 봅니다.',
            },
            {
              term: '변경',
              desc: '결제수단 등록·기본 카드 변경·삭제, 청구서 결제, 제품 신청·해지. 회사 비용이 달라지는 일이라 더 높은 권한이 필요합니다.',
            },
          ]}
        />
        <HelpP>
          <b>결제수단을 지우면 자동결제가 실패합니다.</b> 며칠 지나면 미납으로 서비스가
          멈추므로, 새 카드를 먼저 등록한 뒤 옛 카드를 지우세요.
        </HelpP>

        <HelpH>이번 달 예상과 자동결제 견적은 다릅니다</HelpH>
        <HelpList
          items={[
            {
              term: '이번 달 예상',
              desc: '지금까지의 사정을 그대로 반영한 이번 달 금액입니다. 달 중간에 인원을 늘렸거나 할인이 시작됐으면 날짜만큼만 계산됩니다.',
            },
            {
              term: '자동결제 견적',
              desc: '체험이 끝난 뒤 매달 빠져나갈 금액입니다. 앞으로의 한 달을 묻는 것이라 날짜로 쪼개지 않습니다. 그래서 두 숫자가 다를 수 있습니다.',
            },
          ]}
        />
      </>
    ),
  },

  'ai-reports': {
    title: 'AI 보고서 — 화면 설명',
    body: (
      <>
        <HelpP>
          <HelpPath>자사 현황</HelpPath> 에서 만드신 <b>AI 주간 보고서가 모두 여기 남습니다.</b>{' '}
          <b>다시 보는 데는 요금이 들지 않습니다</b> — 새로 만들 때만 건당 요금이 붙습니다.
        </HelpP>
        <HelpH>왜 남겨 두나</HelpH>
        <HelpP>
          AI 가 쓰는 글이라 <b>같은 기간으로 다시 눌러도 표현이 달라집니다.</b> 그래서 보고에
          쓰신 그 글은 여기서만 그대로 되찾으실 수 있습니다. 예전에는 팝업을 닫으면 사라져서
          다시 보려면 요금을 또 내고 만들어야 했습니다.
        </HelpP>
        <HelpH>새로 만들기</HelpH>
        <HelpP>
          오른쪽 위 <b>[AI 주간 보고서]</b> 를 누르면 만드는 창이 열립니다. 창 위에 이번 달
          무료 수량과 남은 건수, 초과 시 단가가 뜨고, <b>만들 때마다 동의 체크</b>를 받습니다
          (분석할 내용이 선택된 AI 모델로 전달되기 때문입니다).
        </HelpP>
        <HelpH>보고서에 금액·엔진이 없는 이유</HelpH>
        <HelpP>
          보고서는 받아서 보고에 쓰시는 글입니다. 거기에 「○○○원 청구」나 엔진 이름이 붙어
          있으면 보고서가 아니라 영수증처럼 읽힙니다. 그래서 목록에도 보고서에도 적지
          않습니다 — 무료분으로 만든 것과 초과분으로 만든 것이 섞여 있어, 목록을 세로로
          더하면 어차피 청구서와 맞지도 않습니다.
        </HelpP>
        <HelpP>
          청구서의 「AI 보고서 생성」 줄과는 <b>만든 날짜</b>로 맞춰 보실 수 있습니다. 그때의
          단가와 동의 기록은 <b>보고서마다 보관</b>되어 있으니, 확인이 필요하시면 공급사로
          연락 주세요.
        </HelpP>
        <HelpH>지우기</HelpH>
        <HelpP>
          기업을 관리하는 분만 지울 수 있습니다. 지우면 되돌릴 수 없고, 같은 글을 다시 얻을
          방법도 없습니다 — 다시 만들면 요금이 또 붙고 글도 달라집니다.
        </HelpP>
      </>
    ),
  },

  logs: {
    title: '로그 조회 — 화면 설명',
    body: (
      <>
        <HelpP>
          우리 기업에서 일어난 일을 돌아봅니다. 누가 언제 무엇을 보고 고쳤는지가 남습니다.
        </HelpP>
        <HelpH>일곱 가지를 나눠 봅니다</HelpH>
        <HelpList
          items={[
            { term: '감사', desc: '등록·수정·삭제. 무엇이 어떻게 바뀌었는지까지 남습니다.' },
            { term: '로그인', desc: '로그인·로그아웃과 실패한 시도' },
            { term: '세션', desc: '언제 접속해 있었고 어디서 끊겼는지' },
            { term: '메시지', desc: '보낸 알림 메일과 그 결과' },
            { term: '알림', desc: '한도 초과 같은 경고가 울린 기록' },
            { term: '파일', desc: '올리고 내려받은 기록' },
            { term: '설정', desc: '보안 정책처럼 회사 설정을 바꾼 기록' },
          ]}
        />
        <HelpP>
          기간을 정해 찾고, <HelpPath>엑셀 내보내기</HelpPath> 로 받아 둘 수 있습니다.
          내보낸 것 역시 기록으로 남습니다. <b>우리 회사 기록만</b> 보입니다 — 다른
          회사의 것은 섞이지 않습니다.
        </HelpP>
        <HelpH>왜 지울 수 없나</HelpH>
        <HelpP>
          개인정보보호법은 접속기록을 최소 1년 보관하도록 정합니다. 그래서 보존 기간이
          지나기 전에는 지워지지 않고, 기간이 지나면 자동으로 정리됩니다. 보관 기간은{' '}
          <b>서비스 공급사가 정합니다</b> — 기업이 바꿀 수 있는 값이 아닙니다.
        </HelpP>
      </>
    ),
  },

  settings: {
    title: '기업 설정 — 화면 설명',
    body: (
      <>
        <HelpP>
          상호·주소·담당자 같은 우리 기업의 기본 정보를 관리합니다. <b>여기가 원본입니다</b> —
          WORK&CRM·WORK&ECO 가 쓰는 회사 정보도 모두 이 값을 따라갑니다. 제품마다 따로 적을
          필요가 없고, 여기서 한 번 고치면 세 곳이 같이 바뀝니다.
        </HelpP>
        <HelpH>어디에 쓰이나</HelpH>
        <HelpList
          items={[
            { term: '상호', desc: '청구서와 각종 안내 메일, 그리고 제품 화면 상단에 찍힙니다.' },
            {
              term: '담당자 이메일',
              desc: '한도 초과, 체험 만료 같은 알림이 이 주소로 갑니다. 비워 두면 알림을 받지 못합니다.',
            },
            {
              term: '사업자번호 · 주소 · 업태 · 종목',
              desc: 'WORK&ECO 에서 사업장을 등록할 때 기본값으로 채워집니다. 같은 값을 두 번 적지 않아도 됩니다.',
            },
          ]}
        />
        <HelpP>대표자·담당자 이름과 연락처는 암호화해서 보관합니다.</HelpP>
        <HelpP>
          빈 칸으로 저장하면 <b>지우라는 뜻</b>으로 받습니다. 다만 사업자등록번호는
          숫자 10자리, 법인등록번호는 13자리라야 저장됩니다 — 형식이 맞지 않으면
          저장 대신 안내가 뜹니다.
        </HelpP>

        <HelpH>첨부 서류</HelpH>
        <HelpP>
          사업자등록증처럼 증빙이 필요한 서류를 올려 둡니다. 올린 파일은 바로 열리지
          않고 <b>그때그때 만들어지는 한시적인 주소</b>로만 내려받습니다. 그 주소는
          잠시 뒤 만료되므로, 링크를 복사해 두었다가 나중에 열면 다시 받아야 합니다.
          운영자도 같은 서류를 보며 가입 심사를 합니다.
        </HelpP>
        <HelpP>
          <b>사업자등록증 · 통장 사본 · 인감증명서는 한 장만 보관합니다.</b> 새로
          올리시면 기존 파일을 지우고 새 파일로 대체하며, 지우기 전에 <b>무엇이
          지워지는지 보여 드리고 한 번 여쭤 봅니다.</b> 지워진 파일은 휴지통으로 가고
          기록은 남습니다. 주소가 바뀌어 다시 발급받으셨을 때 옛 등록증과 새 등록증이
          나란히 남아 어느 것이 유효한지 알 수 없게 되는 일을 막기 위해서입니다.
          계약서처럼 여러 장이 정상인 종류는 그대로 쌓입니다.
        </HelpP>
        <HelpP>
          <b>사업자등록증이 없으면 세금계산서를 발행해 드릴 수 없습니다.</b> 아직 올리지
          않으셨다면 로그인 직후 <HelpPath>자사 현황</HelpPath> 맨 위에 안내가 뜹니다.
        </HelpP>
        <HelpH>사업자등록증은 올리면 자동으로 읽어 드립니다</HelpH>
        <HelpP>
          <b>사업자등록증</b>을 올리시면 AI 가 읽어 위쪽 칸(<b>상호·사업자등록번호·
          대표자·개업연월일·업태·종목·주소</b>)을 채워 드립니다. 이미 올려 두신 서류는
          목록의 <b>[자동 읽기]</b> 로 다시 읽으실 수 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '바로 저장되지 않습니다',
              desc: '읽은 값을 보여 드리고, 「칸에 채우기」를 누르셔야 칸에 들어갑니다. 저장은 또 한 번 「저장」을 누르셔야 합니다 — 두 번 확인하시는 셈입니다.',
            },
            {
              term: '읽은 값은 칸에 덮어씁니다',
              desc: '서류에서 읽어 낸 칸은 이미 적혀 있던 값 위에 덮습니다 — 주소가 바뀌어 새 등록증을 올리셨을 때 옛 주소가 남아 있으면 안 되기 때문입니다. 무엇으로 덮을지는 「채우기」를 누르시기 전에 창에서 전부 보여 드리고, 읽지 못한 칸은 건드리지 않습니다. 마음에 들지 않으면 「쓰지 않기」를 누르시면 됩니다.',
            },
            {
              term: '사업자등록번호가 비어 있다면',
              desc: '잘못 읽은 것입니다. 사업자등록번호는 검증번호가 있어서, 맞지 않으면 채우지 않고 비워 둡니다 — 틀린 번호를 자신 있게 채워 드리는 것이 가장 나쁩니다. 서류를 보고 직접 입력해 주세요.',
            },
            {
              term: '꼭 확인하고 저장해 주세요',
              desc: '자동으로 읽은 값은 틀릴 수 있습니다. 특히 「주식회사」와 「(주)」, 숫자 0 과 영문 O 가 그렇습니다. 상호와 사업자등록번호가 틀리면 세금계산서가 발행되지 않습니다.',
            },
          ]}
        />

        <HelpH>지원 접근 이력</HelpH>
        <HelpP>
          서비스 공급사 담당자가 문제를 봐 주려고 <b>우리 회사 화면에 들어온 기록</b>
          입니다. 누가 언제 어떤 사유로 들어왔는지가 남고, 그동안 한 일도 모두{' '}
          <HelpPath>로그 조회</HelpPath> 에 기록됩니다. 모르는 접근이 보이면 바로 알려
          주세요.
        </HelpP>

        <HelpH>기업 로고</HelpH>
        <HelpP>
          여기 한 번 올리면 <b>WORK&Hub·WORK&CRM·WORK&ECO 세 화면의 상단</b>에 함께
          나옵니다. 제품 로고는 그대로 왼쪽에 남고, 회사 로고는 회사 이름 옆에 붙습니다.
          배경을 지운 가로형 PNG 가 가장 깨끗하고, 올린 미리보기는 실제와 같은 어두운
          바탕에서 확인할 수 있습니다.
        </HelpP>

        <HelpH>AI 보고서 — 매달 무료 건수가 있습니다</HelpH>
        <HelpP>
          <b>처음부터 돈이 드는 기능이 아닙니다.</b> 제품마다 <b>매달 쓸 수 있는 무료 건수</b>가
          요금제에 들어 있고, 그 안에서는 0원입니다. 무료 건수를 넘긴 건수만 다음 청구서에
          더해집니다. 표의 <b>무료 제공</b> 칸과 <b>이번 달 사용</b> 칸에서 지금 몇 건이 남았는지
          바로 확인하실 수 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '무료 건수는 기업 단위입니다',
              desc: '사람 수와 무관합니다. 구성원이 늘어도 무료 건수는 그대로입니다.',
            },
            {
              term: '매월 1일에 새로 시작하고 이월되지 않습니다',
              desc: '지난달에 안 쓰고 남긴 건수는 사라집니다.',
            },
            {
              term: '제품마다 따로 셉니다',
              desc: 'WORK&Hub 의 무료분을 WORK&CRM 에서 당겨 쓸 수 없습니다.',
            },
            {
              term: '실패하면 건수에서 빼지 않습니다',
              desc: '만들다 오류가 난 건은 무료 건수도 요금도 쓰지 않습니다.',
            },
          ]}
        />
        <HelpP>
          <b>쓸지 말지는 고르지 않습니다</b> — 세 제품 요금제에 모두 들어 있습니다. 이 탭에서
          고르는 것은 <b>어느 엔진으로 돌릴지</b> 하나뿐입니다(Claude 또는 Gemini, 기본값
          Claude). 어느 쪽을 골라도 <b>무료 건수와 요금은 같고</b>, 고른 값은 세 제품에 함께
          적용됩니다.
        </HelpP>
        <HelpP>
          API 키와 모델은 서비스 공급사가 관리하므로 따로 준비하실 것이 없습니다. 한 번에
          분석하는 기간은 <b>최대 1개월</b>입니다 — 자료가 많을수록 요약이 뭉개지고 비용도
          늘어납니다.
        </HelpP>

        <HelpH>메일·AI 는 여기서 설정하지 않습니다</HelpH>
        <HelpP>
          알림 메일의 발송 계정과 AI API 키는 <b>서비스 공급사가 한 곳에서 관리</b>합니다.
          기업이 따로 넣을 것이 없고, WORK&CRM 회사정보에 있던 SMTP·AI 키 칸도 그래서
          없어졌습니다.
        </HelpP>

        <HelpH>보안 정책 — 바꾸면 사유가 남습니다</HelpH>
        <HelpP>
          이 탭의 값을 고치면 <b>왜 바꾸는지</b>를 적는 창이 뜹니다. 적은 사유는 감사 기록에
          남아 나중에 누가 무엇을 왜 낮췄는지 되짚을 수 있습니다. 저장은 <b>탭 전체가 한 번에</b>
          됩니다 — 2차 인증만 고쳐도 비밀번호·세션 값이 함께 저장됩니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '관리자 강제가 인증 방식보다 셉니다',
              desc: '인증 방식을 OFF 로 두어도 이 스위치가 켜져 있으면 관리자에게는 계속 2차 인증을 요구합니다. "껐는데 왜 코드를 묻느냐" 의 답이 거의 여기 있습니다.',
            },
            {
              term: '세션 정책은 지금 로그인한 사람에게도 적용됩니다',
              desc: '「1개만 허용」으로 바꾸면 여러 기기로 들어와 있던 사람이 다음 접속에서 밀려납니다. 업무 시간에 바꾸면 그 자리에서 튕깁니다.',
            },
            {
              term: '유출 이력 비밀번호 차단은 끌 수 없습니다',
              desc: '외부에 유출된 적이 있는 비밀번호는 항상 막습니다. 이것만은 기업이 끄지 못합니다.',
            },
            {
              term: '비밀번호 규칙은 다음 변경부터',
              desc: '최소 길이를 올려도 이미 쓰고 있는 비밀번호가 즉시 막히지는 않습니다. 다음에 바꿀 때 새 규칙을 적용합니다.',
            },
          ]}
        />

        <HelpH>2차 인증을 껐는데도 코드를 묻는다면</HelpH>
        <HelpP>
          <HelpPath>보안 정책</HelpPath> 의 <b>관리자 강제</b> 를 함께 확인하세요. 이 스위치는
          인증 방식보다 세서, 방식을 OFF 로 두어도 켜져 있으면 관리자에게는 계속 2차 인증을
          요구합니다.
        </HelpP>

        <HelpH>서비스 종료 — 두 단계입니다</HelpH>
        <HelpP>
          <b>1단계: 신청.</b> 안내문을 읽고 사유를 적어 신청하면, 신청하신 분의 메일로{' '}
          <b>6자리 확인 코드</b>가 갑니다. 이 시점에는 <b>아무것도 해지되지 않습니다</b> —
          구독도 로그인도 그대로입니다.
        </HelpP>
        <HelpP>
          <b>2단계: 확인.</b> 그 코드를 화면에 넣으셔야 정산·결제·구독 해지가 실제로
          돌아갑니다. 코드를 넣지 않으시면 유효 시간(기본 15분)이 지난 뒤 저절로 취소되고
          아무 일도 일어나지 않습니다.
        </HelpP>
        <HelpP>
          <b>왜 둘로 나눴나.</b> 예전에는 단추 한 번으로 끝까지 갔습니다. 잘못 눌렀을 때
          되돌릴 자리가 없었고, 비밀번호만 알아내면 남의 기업을 통째로 지울 수 있었습니다.
          코드를 메일로 보내면 <b>메일함까지 가진 사람만</b> 종료할 수 있고, 두 단계 사이가
          자연스러운 <b>취소 지점</b>이 됩니다.
        </HelpP>
        <HelpP>
          코드를 기다리는 동안 화면에 <b>[신청 취소]</b> 단추가 보입니다. 누르면 없던 일이
          됩니다. 코드를 다섯 번 잘못 넣어도 신청이 취소되니, 그때는 다시 신청하시면 됩니다.
        </HelpP>
        <HelpH>종료할 때 얼마가 청구되나</HelpH>
        <HelpP>
          누르기 <b>전에</b> 화면에 합계가 나옵니다. 네 가지가 더해지고 빠집니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '① 이용료 정산 (더함)',
              desc: '사용한 기간 동안 비용이 청구 됩니다. 마지막 청구 다음 날부터 종료일까지가 계산되고 남은 날짜분은 청구하지 않습니다 — 55,000원짜리를 5일에 끝내면 55,000 × 5/31 = 8,871원입니다. 저장 용량·트래픽 초과분은 실제로 쓴 양 그대로 더해집니다.',
            },
            {
              term: '② 백업 비용 (더함)',
              desc: '백업을 고르셨을 때만 붙습니다. 고르지 않으면 0원입니다.',
            },
            {
              term: '③ 약정 할인 반환 (더함)',
              desc: '연 약정을 채우기 전에 그만두시면 그동안 받으신 할인액을 되돌려 받습니다. 따로 떼는 위약금은 없습니다. 제품별 금액이 화면에 적힙니다.',
            },
            {
              term: '④ 환불 차감 (뺌)',
              desc: '이전에 돌려드리기로 한 금액이 있으면 이 정산에서 깎습니다. 합계에 이미 반영되어 있고, 왜 줄었는지 알 수 있도록 줄로도 보여 드립니다.',
            },
          ]}
        />
        <HelpP>
          화면의 합계는 <b>부가세 별도</b>입니다 — 실제 청구서에는 부가세가 더해집니다. 금액이
          0원이어도 종료는 진행됩니다(체험 중이거나 이미 정산이 끝난 경우).
        </HelpP>

        <HelpH>종료하면 누가 언제까지 들어올 수 있나</HelpH>
        <HelpP>
          <b>구성원은 즉시 막힙니다.</b> 기업 소유자·관리자만 보관 기간 동안 로그인이
          유지됩니다 — 신청하신 백업 자료를 받으시라고 열어 두는 문입니다. 그 기간에도
          WORK&ECO·WORK&CRM 화면은 열리지 않습니다(구독이 해지되기 때문입니다). 필요한
          자료는 <b>종료 전에</b> 내려받아 두셔야 합니다.
        </HelpP>
        <HelpP>
          보관 일수와 관리자 로그인 일수는 <b>같은 값으로 운영합니다.</b> 다르게 두면
          「자료는 서버에 있는데 주인이 꺼낼 수 없는」 기간이 생기고, 그 기간에 들어온 문의는
          답할 말이 없습니다. 두 값 모두 시스템 관리자 Hub 의{' '}
          <HelpPath>시스템 → 공통코드 → TERMINATION_POLICY</HelpPath> 에서 정합니다
          (<b>PURGE_AFTER_DAYS</b> · <b>GRACE_LOGIN_DAYS</b>, 그리고 영구 삭제 예고를 며칠
          전에 보낼지 정하는 <b>PURGE_NOTICE_LEAD_DAYS</b>). 배포 없이 바꿀 수 있고, 안내문과
          메일·홍보 사이트 요금 안내가 모두 이 값을 받아 씁니다.
        </HelpP>

        <HelpH>신청하신 백업 자료 받기</HelpH>
        <HelpP>
          종료가 확정되면 신청하신 항목이 <b>자동으로 만들어져</b> 이 화면의{' '}
          <b>「신청하신 백업 자료」</b> 에 뜹니다. 몇 분 걸릴 수 있고, 다 되면 상태가
          <b> 「받으실 수 있습니다」</b> 로 바뀝니다. 압축 안의 파일은{' '}
          <b>올리실 때의 이름 그대로</b>이고 제품별 폴더로 나뉘어 있습니다.
        </HelpP>
        <HelpP>
          WORK&ECO·WORK&CRM 의 업무 자료는 <b>각 제품이 만들어 올립니다.</b> 그쪽
          데이터는 제품의 별도 데이터베이스에 있어서, WORK&Hub 가 직접 꺼내지 않고
          제품이 주기적으로 「만들 것 있나」를 물어보러 옵니다 — Hub 가 모든 제품의
          데이터베이스 비밀번호를 들고 있지 않기 위해서입니다. 그래서 신청 직후
          <b> 몇 분 동안은 「대기 중」</b>으로 보일 수 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: 'WORKnHub 자료',
              desc: '기업정보·조직도·구성원·역할권한·파일목록을 CSV 로 담습니다. 엑셀에서 바로 열립니다(한글이 깨지지 않도록 BOM 을 붙입니다).',
            },
            {
              term: 'WORK&Hub 기업 문서',
              desc: 'WORK&Hub 에 올리신 파일(사업자등록증 등)을 원본 이름 그대로 담습니다. 양이 많으면 여러 조각(1/3, 2/3 …)으로 나뉘며, 조각 하나에도 안 들어가는 큰 파일은 「_받지_못한_파일.txt」 에 적어 둡니다 — 그 경우 연락 주시면 따로 보내 드립니다. 제품에 올리신 첨부는 여기가 아니라 아래 제품 자료에 들어 있습니다.',
            },
            {
              term: 'WORK&ECO · WORK&CRM 자료',
              desc: '각 제품이 자기 데이터베이스에서 CSV 로 뽑고, 그 제품에 올리신 첨부파일 본문까지 「첨부파일」 폴더에 함께 담아 올립니다. 올라오기 전에는 「대기 중」으로 보이고, 보통 몇 분 안에 「받으실 수 있습니다」로 바뀝니다. 자료가 아주 커서 한 번에 못 올리는 경우에는 사유가 화면에 뜨고 공급사가 따로 전달해 드립니다.',
            },
          ]}
        />
        <HelpP>
          자료는 <b>보관 기간이 끝나면 함께 삭제</b>됩니다. 관리자 계정이 그때까지
          로그인되는 이유가 이것입니다 — 기간이 모자라시면 연락 주시면 늘려 드립니다.
          누가 언제 받아 갔는지는 감사로그에 남습니다.
        </HelpP>

        <HelpH>되돌리려면</HelpH>
        <HelpP>
          <b>확인 코드를 넣기 전</b>이면 화면의 [신청 취소]로 직접 되돌리실 수 있습니다.
        </HelpP>
        <HelpP>
          <b>이미 확정된 뒤</b>에는 화면에서 되돌릴 수 없습니다. 정산·결제·구독 해지가 모두
          끝난 상태라 상태 한 줄만 바꾸면 「구독은 살아났는데 청구는 종료된」 식으로
          어긋나기 때문입니다. 보관 기간이 지나기 전에 공급사로 연락하시면 운영자가
          되살립니다. 기간이 지나 영구 삭제된 뒤에는 <b>복구할 방법이 없습니다</b> —
          공급사에도 사본이 남지 않습니다.
        </HelpP>
      </>
    ),
  },

  account: {
    title: '내 계정 — 화면 설명',
    body: (
      <>
        <HelpP>내 정보와 비밀번호, 2차 인증을 관리합니다.</HelpP>
        <HelpH>2차 인증</HelpH>
        <HelpP>
          켜면 로그인할 때 비밀번호 외에 일회용 코드를 더 입력합니다. 비밀번호가 새어도
          계정을 지킬 수 있습니다.
        </HelpP>
        <HelpH>로그인한 기기</HelpH>
        <HelpP>
          지금 로그인되어 있는 기기 목록을 보고, 모르는 기기는 끊을 수 있습니다. 끊으면 그
          기기는 즉시 로그아웃됩니다.
        </HelpP>

        <HelpH>언제 자동으로 로그아웃되나</HelpH>
        <HelpList
          items={[
            { term: '30분 동안 아무 것도 안 하면', desc: '자리를 비운 사이 끊깁니다.' },
            { term: '로그인한 지 12시간이 지나면', desc: '계속 쓰고 있어도 끊깁니다.' },
            {
              term: '다른 기기에서 로그인하면',
              desc: '한 번에 한 기기만 쓸 수 있어 먼저 있던 쪽이 끊깁니다.',
            },
          ]}
        />
        <HelpP>
          시간은 기업마다 다를 수 있습니다(<HelpPath>기업 설정 → 보안 정책</HelpPath>).
          끊긴 뒤 화면을 누르면 로그인 창으로 가며 <b>왜 끊겼는지</b>를 알려 줍니다.
        </HelpP>
      </>
    ),
  },

  /* ---- 플랫폼 운영자 ---- */

  overview: {
    title: '운영 현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          플랫폼 전체를 봅니다. 기업 수, 구독 상태, 이번 달 사용량과 알람이 모입니다.
        </HelpP>

        <HelpH>맨 위의 빨간 띠 — 조치가 필요한 것만 뜹니다</HelpH>
        <HelpList
          items={[
            {
              term: '한도에 막힌 기업',
              desc: (
                <>
                  초과 사용이 <b>허용되지 않는</b> 항목이라 그 기능이 멈춘 기업입니다.
                  요금제를 올리거나 한도를 조정해야 풀립니다.
                </>
              ),
            },
            { term: '연체 금액', desc: '납기를 넘긴 청구서의 합입니다. 과금 관리 화면에서 봅니다.' },
            {
              term: '로그인 실패 급증',
              desc: '최근 3일 실패가 성공보다 훨씬 많으면 뜹니다. 무차별 대입 시도일 수 있습니다.',
            },
          ]}
        />
        <HelpP>
          <b>포함량을 넘겨 추가 과금되는 것은 띄우지 않습니다.</b> 그건 사고가 아니라
          매출이고 기능도 그대로 돌아갑니다. AI 보고서처럼 포함량이 0인 종량제 항목은
          한 건만 써도 「넘김」으로 잡히기 때문에, 섞어 세면 멀쩡한 기업이 조치 대상으로
          뜹니다.
        </HelpP>

        <HelpH>알람</HelpH>
        <HelpP>
          결제 실패·저장용량 초과·로그인 연속 실패 같은 일이 생기면 여기에 뜹니다. 확인
          처리를 하면 목록에서 내려갑니다.
        </HelpP>

        <HelpH>맨 아래 표는 상위 10곳만입니다</HelpH>
        <HelpP>
          여기는 아침에 한 번 훑는 자리라 <b>전부 늘어놓지 않습니다.</b> 오른쪽 위에서 고른
          기준(<b>용량순 · 트래픽순 · 사용자순</b>)으로 위에서 10곳만 보여 주고, 표 아래에
          전체가 몇 곳인지 적어 둡니다. 검색하거나 전부 보시려면 거기 있는 링크로{' '}
          <HelpPath>고객 → 기업별 사용량</HelpPath> 으로 가십시오.
        </HelpP>
        <HelpList
          items={[
            {
              term: '저장 용량',
              desc: '그 기업이 올려 둔 파일의 합계입니다. 계약 한도가 있으면 막대로 함께 보여 줍니다.',
            },
            {
              term: '파일 수',
              desc: '보관 중인 파일 개수입니다. 용량과 같이 읽습니다 — 같은 1GB 라도 큰 파일 열 개와 작은 파일 십만 개는 백업·이관에 드는 품이 전혀 다릅니다.',
            },
            {
              term: '이번 달 트래픽',
              desc: '내려받기로 나간 양입니다. 매월 1일에 0 부터 다시 시작합니다.',
            },
          ]}
        />
      </>
    ),
  },

  tenants: {
    title: '기업 관리 — 화면 설명',
    body: (
      <>
        <HelpP>
          이 플랫폼을 쓰는 고객사를 관리합니다. 기업을 새로 만들면 그 기업의 첫 관리자에게
          초대 메일이 갑니다.
        </HelpP>
        <HelpH>목록의 숫자가 뜻하는 것</HelpH>
        <HelpList
          items={[
            {
              term: '사용자 / 활성',
              desc: '그 기업의 전체 계정과, 그중 쓰고 있는 계정입니다. 초대만 해 두고 들어오지 않은 사람은 활성에 들어가지 않습니다.',
            },
            {
              term: '제품 꼬리표',
              desc: '구독 중인 제품과 요금제입니다. 제품마다 계약 인원이 따로이므로, 기업 사용자 수와 제품별 인원은 다를 수 있습니다.',
            },
            {
              term: '다음 주기',
              desc: '구독 중 가장 이른 청구 주기의 끝입니다. 제품을 둘 쓰면 둘 중 빠른 쪽입니다.',
            },
          ]}
        />

        <HelpH>지원 모드</HelpH>
        <HelpP>
          고객사가 요청하면 그 기업의 화면을 대신 들어가 볼 수 있습니다. 들어가 있는 동안
          화면 위에 빨간 띠가 뜨고, <b>모든 조회와 변경이 기록</b>됩니다. 정해진 시간이
          지나면 자동으로 끊깁니다.
        </HelpP>
        <HelpH>기업을 누르면 열리는 상세 창</HelpH>
        <HelpP>
          창 안에서 위아래로 스크롤합니다. <b>기업 정보 탭은 화면보다 길어</b> 첨부 서류가
          아래쪽에 있습니다 — 보이지 않으면 창 안을 내려 보세요.
        </HelpP>
        <HelpList
          items={[
            { term: '기업 정보', desc: '상호·사업자번호·대표자·주소·담당자를 보고 그 자리에서 고칩니다. 기업 쪽에서도 설정 → 기업 설정에서 같은 내용을 고칠 수 있습니다. 같은 탭 아래쪽에 사업자등록증·통장사본 같은 첨부 서류가 있고, 사업자등록증은 「자동 읽기」로 AI 가 읽어 칸을 채워 줍니다.' },
            { term: '제품', desc: '쓰고 있는 제품과 요금제, 계약 인원, 이번 주기의 끝, 구독 상태입니다. 청구 금액이 왜 그만큼인지는 여기 계약 인원에서 시작합니다.' },
            {
              term: '결제 수단',
              desc: (
                <>
                  <b>지금 어떻게 내고 있는가</b>가 맨 위에 나옵니다 — 계좌이체인지 카드인지,
                  언제 골랐는지, 입금자명을 적었는지. 그 아래는 자동결제 동의 이력인데, 이
                  기록은 <b>카드를 등록할 때만</b> 남습니다. 계좌이체로 내는 기업은 이력이
                  비어 있는 것이 정상입니다.
                </>
              ),
            },
            { term: '가입 기록', desc: '가입할 때 약관·개인정보처리방침에 동의한 시각과 등록자 IP 가 남습니다. 분쟁이 생기면 이것이 근거가 됩니다.' },
            { term: '인원 변경', desc: '계약 인원을 넘겨 사용자를 늘릴 때 누가 언제 승인했고 월 얼마가 늘었는지가 쌓입니다.' },
            { term: '일괄등록 동의 · 서비스 종료', desc: '해당하는 기록이 있는 기업에만 탭이 뜹니다. 없는 기업에 빈 탭을 띄우지 않습니다.' },
          ]}
        />

        <HelpH>입금자명을 꼭 확인하세요</HelpH>
        <HelpP>
          계좌이체는 통장에 <b>보낸 사람의 이름</b>만 찍힙니다. 그 이름이 상호와 다른 일이
          흔합니다 — 대표자 개인 이름, 경리 담당자 이름, 「(주)」가 빠진 상호. 고객이 미리
          적어 둔 입금자명이 결제 수단 탭에 나오니, 입금 내역과 맞춰 보실 때 쓰십시오.{' '}
          <b>잘못 맞추면 낸 기업이 미납으로 남아 정지됩니다.</b>
        </HelpP>

        <HelpH>다른 화면과의 연결</HelpH>
        <HelpList
          items={[
            { term: '구독·청구', desc: '기업마다 어떤 요금제를 쓰는지 정합니다.' },
            { term: '제품 현황', desc: '기업이 어떤 제품을 쓰는지 봅니다.' },
            { term: '매출 현황', desc: '여기 기업들의 결제가 모여 월별 매출이 됩니다.' },
          ]}
        />
      </>
    ),
  },

  products: {
    title: '제품 현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          플랫폼에 등록된 제품(WORK&ECO 등)을 관리합니다. 여기 등록해야 고객사가 신청할 수
          있습니다.
        </HelpP>
        <HelpH>중요한 두 가지 주소</HelpH>
        <HelpList
          items={[
            {
              term: '접속 URL',
              desc: '고객사 화면의 「바로가기」가 여는 주소입니다. 비어 있으면 버튼이 잠깁니다.',
            },
            {
              term: '상태 확인 URL',
              desc: (
                <>
                  제품이 살아 있는지 확인하려고 서버가 직접 부르는 주소입니다. 같은 서버
                  안이라면 공인 주소가 아니라 <b>내부 주소</b>를 넣는 편이 정확합니다
                  (예: <code>http://workeco-api:3100/api/health</code>). 비워 두면
                  「상태 확인 주소가 등록되지 않았습니다」로 뜨고 점검이 돌지 않습니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>「사용자」는 그 제품을 쓰는 사람만 셉니다</HelpH>
        <HelpP>
          기업 전체 계정 수가 아닙니다. <b>그 제품 역할을 받은 사람</b>과 기업
          소유자·관리자만 셉니다(관리자는 역할과 무관하게 모든 제품에 들어갈 수 있어
          늘 포함됩니다).
        </HelpP>
        <HelpP>
          그래서 WORK&CRM 15명 · WORK&ECO 3명을 쓰는 기업 하나가 있으면 CRM 은 15,
          ECO 는 3 입니다. 예전에는 기업 전체 계정을 더해 <b>ECO 도 15명</b>으로
          보였는데, 계약도 청구도 3명이라 숫자가 서로 맞지 않았습니다. 지금은
          <b>좌석 판정·청구서와 같은 기준</b>으로 셉니다.
        </HelpP>

        <HelpH>구독 기업 목록의 「계약」과 「실사용」</HelpH>
        <HelpP>
          둘이 다를 수 있습니다. 요금제에 <b>최소 인원</b>이 있어서, 실제로 쓰는 사람이
          그보다 적어도 최소 인원만큼 청구됩니다(예: 실사용 1명인데 계약 6명). 반대로
          실사용이 계약을 넘으면 야간 배치가 계약 수량을 올립니다.
        </HelpP>

        <HelpH>서비스 키</HelpH>
        <HelpP>
          제품이 플랫폼에 사용 현황을 보고할 때 쓰는 열쇠입니다. 발급해서 제품 설정에 넣어
          두면 사용량이 집계됩니다. <b>쓰지 않는 키는 폐기하세요</b> — 개발용으로 만든
          키가 남아 있으면 그 키로 사용량을 조작할 수 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '평문은 발급할 때 한 번만',
              desc: '다시 볼 수 없습니다. 그 자리에서 복사해 제품 설정에 넣으세요. 잃어버리면 새로 발급하고 옛 키를 폐기합니다.',
            },
            {
              term: '폐기하면 즉시 막힙니다',
              desc: '다음 호출부터 401 입니다. 제품이 아직 그 키를 쓰고 있으면 사용량 보고가 끊기므로, 새 키를 먼저 넣고 옛 키를 폐기하세요.',
            },
            {
              term: '목록은 사용 중인 키만',
              desc: '폐기한 키는 지우지 않고 남으므로 그냥 두면 쌓입니다. 지난 키가 필요하면 「폐기한 키 포함」을 켜세요.',
            },
            {
              term: '제품을 넘나들 수 없습니다',
              desc: 'CRM 키로 ECO 경로를 부르면 거절됩니다. 키는 자기 제품의 사용량 보고·설정 조회·알림 메일만 할 수 있고, 고객 데이터에는 닿지 않습니다.',
            },
          ]}
        />

        <HelpH>구독하지 않은 제품은 기업 자료를 받지 못합니다</HelpH>
        <HelpP>
          제품은 각자 따로 배포된 서버입니다. 그래서 <b>그 기업이 구독한 제품에만</b>{' '}
          기업 정보와 구성원 명부를 건넵니다. 구독하지 않은 제품이 물으면{' '}
          <HelpPath>PRODUCT_NOT_SUBSCRIBED</HelpPath> 로 거절되고, 제품은 「구독이
          필요합니다」 화면을 띄웁니다.
        </HelpP>
        <HelpP>
          구독을 정지하거나 해지하면 그 순간부터 막힙니다. 제품 쪽에서 「회사 정보가
          안 보인다」는 문의가 오면 <HelpPath>과금 &gt; 구독·청구</HelpPath> 에서 그
          기업의 구독 상태부터 확인하세요.
        </HelpP>
      </>
    ),
  },

  payments: {
    title: '수납 내역 — 화면 설명',
    body: (
      <>
        <HelpP>
          <b>실제로 들어온 돈</b>을 봅니다. 카드·계좌이체별 입금액, 실패한 자동결제와 그
          사유, 통장에 찍힌 입금자명까지 한 자리에 모입니다.
        </HelpP>

        <HelpH>여기서는 입금을 「등록」하지 않습니다</HelpH>
        <HelpP>
          이 화면은 <b>보는 곳</b>입니다. 계좌이체 입금을 새로 적으려면{' '}
          <HelpPath>구독·청구 → 청구서</HelpPath> 에서 그 청구서 줄의{' '}
          <b>「수납 기록」</b> 단추를 누르세요. <b>어느 청구서에 넣을지를 골라야</b>{' '}
          하므로 청구서 쪽에 있습니다. 적고 나면 그 내역이 이 목록에 나타납니다.
        </HelpP>
        <HelpP>
          여기서 할 수 있는 일은 <b>환불</b>입니다. 이미 받은 돈을 돌려줄 때 그 줄의
          「환불」을 누릅니다.
        </HelpP>

        <HelpH>구독·청구 화면과 어떻게 다른가</HelpH>
        <HelpList
          items={[
            {
              term: '구독·청구',
              desc: '청구서 단위로 봅니다 — 이 청구서가 완납인가, 미납액이 얼마인가.',
            },
            {
              term: '수납 내역',
              desc: '결제 건 단위로 봅니다 — 얼마가 어떤 수단으로 들어왔나, 왜 실패했나, 몇 번째 시도인가.',
            },
          ]}
        />
        <HelpH>결제가 실패했을 때 보는 곳</HelpH>
        <HelpP>
          청구서에는 「미납」이라고만 뜹니다. 고객에게 <b>무엇을 고치라고 말해야 하는지</b>는
          여기서 확인합니다 — 한도 초과인지 카드 유효기간이 지났는지가 실패 사유에 남습니다.
        </HelpP>
        <HelpH>입금자명이 상호와 다를 때</HelpH>
        <HelpP>
          계좌이체는 보낸 사람 이름이 상호와 다른 경우가 많습니다(대표자 개인 명의 등).
          입금자명 칸을 보고 어느 기업의 입금인지 맞춰 봅니다.
        </HelpP>
        <HelpH>위쪽 다섯 칸</HelpH>
        <HelpP>
          지금 걸어 둔 조건 전체의 합입니다. 목록에 보이는 줄만 더한 값이 아니므로, 쪽을
          넘겨도 숫자가 흔들리지 않습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '결제 합계',
              desc: '걸린 결제들의 금액 합입니다. 환불을 빼지 않은 총액이라 목록을 눈으로 더해 검산할 수 있습니다. 전액 환불된 건도 결제는 일어났으므로 들어갑니다.',
            },
            {
              term: '환불 누계',
              desc: '그 결제들에 대해 지금까지 돌려준 금액입니다. 환불한 날짜와는 무관합니다.',
            },
            { term: '순액', desc: '결제 합계 − 환불 누계.' },
          ]}
        />
        <HelpP>
          <b>「순액」과 <HelpPath>매출 현황</HelpPath> 의 「수납」은 다를 수 있습니다.</b>{' '}
          매출 현황은 기간 손익이라 환불을 <b>돌려준 달</b>에서 빼는데, 이 표는 목록에
          담긴 결제를 기준으로 셉니다. 10월에 받은 돈을 11월에 환불하면 10월 순액은
          줄지만 10월 매출의 수납은 그대로입니다 — 달을 넘겨 환불할 때만 생기는
          차이입니다.
        </HelpP>

        <HelpH>입금일을 소급해 적을 수 있습니다</HelpH>
        <HelpP>
          수납을 기록할 때 <b>실제 입금일</b>을 넣습니다. 10월 31일에 들어온 돈을 11월 3일에
          기록해도 <b>10월 수납</b>으로 잡힙니다 — 월말 입금은 흔하고, 어긋난 달은 세무
          자료와 맞지 않습니다. 아직 오지 않은 날짜는 넣을 수 없습니다.
        </HelpP>

        <HelpH>부분 수납과 초과 입금</HelpH>
        <HelpList
          items={[
            {
              term: '일부만 들어왔을 때',
              desc: '그대로 기록하면 됩니다. 청구서는 「부분 수납」이 되고 남은 금액은 미수금에 그대로 남습니다.',
            },
            {
              term: '청구액보다 많이 들어왔을 때',
              desc: '기록이 거절됩니다. 남은 금액보다 많이 받은 것으로 적으면 미수금이 음수가 되어 집계가 망가집니다. 환불하거나 다음 청구서에서 차감해 주세요.',
            },
          ]}
        />
      </>
    ),
  },

  'admin-billing': {
    title: '구독·청구 — 화면 설명',
    body: (
      <>
        <HelpP>기업마다 어떤 요금제를 언제부터 쓰는지 정하고, 청구서를 발행합니다.</HelpP>

        <HelpH>위쪽 네 칸</HelpH>
        <HelpList
          items={[
            {
              term: '미수금',
              desc: '발행했는데 아직 다 안 들어온 금액입니다. 부분 수납된 청구서는 남은 만큼만 셉니다.',
            },
            { term: '연체 금액 · 건수', desc: '그중 납기가 지난 것입니다.' },
            {
              term: '이번 달 수납',
              desc: (
                <>
                  이번 달에 <b>실제로 들어온 돈</b>입니다(입금일 기준, 환불 제외).{' '}
                  <HelpPath>매출 현황</HelpPath> 의 「수납」과 같은 숫자입니다. 청구서가
                  완납되었는지와는 무관하게, 들어온 만큼 셉니다.
                </>
              ),
            },
          ]}
        />

        <HelpH>청구 주기는 매월 21일에 끊습니다</HelpH>
        <HelpP>
          청구 기간은 <b>전월 21일 ~ 당월 20일</b>이고, <b>21일 새벽 6시</b>에 그 기간이
          일괄 청구됩니다. 20일까지의 사용량이 모두 확정된 뒤에 계산합니다(사용량
          재집계 03시 → 차이 계산 04시 → 발행 06시).
        </HelpP>
        <HelpList
          items={[
            {
              term: '발행일을 바꾸려면',
              desc: 'BILLING_POLICY 공통코드의 INVOICE_ISSUE_DAY 를 고칩니다. 21로 두면 기간은 전월 21일~당월 20일, 1로 두면 달력 달(1일~말일)이 됩니다. 고객 화면의 「청구 일정」도 같은 값을 읽으므로 함께 바뀝니다.',
            },
            {
              term: '달 중간에 가입한 기업',
              desc: '청구 기간은 모든 기업이 같고, 금액만 실제 쓴 날만큼 일할 계산됩니다. 10월 25일에 가입했으면 기간은 10/21~11/20, 금액은 25일부터입니다.',
            },
            {
              term: '수동으로 둔 기업',
              desc: '배치가 건드리지 않습니다. 같은 기간이 「일괄 청구」 탭에 후보로 뜨니, 금액을 보고 고른 것만 보내세요.',
            },
            {
              term: '발행을 통째로 멈추려면',
              desc: 'BILLING_POLICY 의 AUTO_INVOICE_ENABLED 를 false 로 둡니다. 자동 발행도 「지금 발행」도 멈춥니다.',
            },
          ]}
        />

        <HelpH>청구서를 발행할 때 — 이용 기간을 먼저 봅니다</HelpH>
        <HelpP>
          이용 기간은 <b>쓴 기간</b>입니다. 발행일·납기일이 아닙니다. 발행 창을 열면{' '}
          <b>배치와 같은 주기</b>가 기본으로 들어가 있습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '아직 시작하지 않은 기간은 거절합니다',
              desc: '10월에 「2026-12」를 고르면 발행되지 않습니다. 쓰지도 않은 달의 요금을 청구하는 일이 날짜 한 칸 잘못 고른 것으로 일어나면 안 되기 때문입니다.',
            },
            {
              term: '기간이 겹치면 거절합니다',
              desc: '11/01~11/30 을 발행한 뒤 11/01~11/29 로 한 번 더 누르면 막히고, 이미 있는 청구서 번호를 알려 줍니다. 같은 달을 두 번 청구하지 않게 합니다. 다시 발행해야 한다면 먼저 그 청구서를 무효화하세요.',
            },
            {
              term: '같은 기간을 두 번 누르면',
              desc: '새로 만들지 않고 이미 있는 청구서를 그대로 돌려줍니다. 단추를 두 번 눌러도 두 장이 나가지 않습니다.',
            },
            {
              term: '이미 나간 달은 고르는 순간 알려 드립니다',
              desc: '발행 창에서 대상 월을 고르면, 그 기간에 이미 나간 청구서가 있을 때 번호·금액·발행일이 노란 안내로 뜨고 [발행] 단추가 꺼집니다. 예전에는 눌러 봐야 거절되어, 그제야 달을 잘못 골랐다는 것을 알았습니다.',
            },
          ]}
        />

        <HelpH>발행하면 바로 메일이 나갑니다</HelpH>
        <HelpP>
          기업의 <b>소유자·관리자</b>에게 금액·납기와 함께 <b>청구서 열람 링크</b>가
          메일로 갑니다. <b>자동·수동·일괄 어느 쪽으로 발행해도</b> 나갑니다 —
          예전에는 자동 발행에만 붙어 있어서, 손으로 보낸 청구서는 고객이 모르는 채로
          납기를 넘겼습니다.
        </HelpP>
        <HelpP>
          링크를 열면 <b>그 기업의 사업자등록번호</b>를 묻고, 맞아야 내역이 보입니다.
          로그인이 필요 없어 Hub 계정이 없는 경리 담당자에게 그대로 전달할 수 있고,
          링크를 주운 사람은 번호를 모르면 열지 못합니다. 번호를 다섯 번 틀리면 30분
          잠기고, 링크는 180일 뒤 만료됩니다. 받을 관리자가 한 명도 없으면 서버
          로그에 경고가 남습니다.
        </HelpP>

        <HelpH>구독 한 줄에서 할 수 있는 일</HelpH>
        <HelpList
          items={[
            { term: '청구서 발행', desc: '이용 기간을 고르고 금액을 미리 본 뒤 발행합니다. 정지된 구독은 청구 대상이 아니라 단추가 꺼져 있습니다.' },
            {
              term: '수량',
              desc: '계약 수량을 고칩니다. 청구액은 실제 사용자 수가 아니라 이 숫자로 계산합니다. 사용자당 과금이면 「그 제품을 쓰는 사람」 아래로는 줄일 수 없습니다 — 줄이는 순간 누군가 제품에 들어가지 못하게 되기 때문입니다. 기준은 전사 인원이 아니라 제품별 인원입니다. 전사 15명이어도 ECO 를 쓰는 사람이 3명이면 ECO 계약은 3까지 내릴 수 있습니다.',
            },
            { term: '할인', desc: '아래 「할인」 항목을 보세요.' },
            {
              term: '요금제',
              desc: '같은 제품 안에서 요금제를 바꿉니다. 판매 중인 요금제만 고를 수 있습니다.',
            },
            {
              term: '상태',
              desc: '정지·재개·해지. 정지하면 고객은 그 제품을 열 수 없고, 정지된 기간은 청구하지 않습니다. 해지는 「기간 말 해지」로 예약할 수 있습니다.',
            },
          ]}
        />
        <HelpP>
          표 오른쪽 위 <b>「신규 구독」</b> 으로 아직 안 쓰는 제품을 붙입니다. 고른
          요금제가 제품을 정하고, 요금제에 무료 체험 일수가 있으면 체험으로 시작합니다.
          이미 그 제품을 구독 중이면 거절되니 그때는 「요금제」로 바꾸세요.
        </HelpP>

        <HelpH>요금제·수량을 달 중간에 바꾸면 — 날짜로 나눠 받습니다</HelpH>
        <HelpP>
          바꾼 <b>그날부터</b> 새 조건입니다. 그달 청구서에는 옛 조건과 새 조건이{' '}
          <b>두 줄로 나뉘어</b> 나옵니다.
        </HelpP>
        <HelpP>
          예) 10월 26일에 15명 → 20명으로 늘리면
          <br />
          <HelpPath>WORK&CRM 사용자 15 · 2026-10-01~2026-10-25 25/31일</HelpPath> 66,532원
          <br />
          <HelpPath>WORK&CRM 사용자 20 · 2026-10-26~2026-10-31 6/31일</HelpPath> 21,290원
        </HelpP>
        <HelpP>
          예전에는 바꾼 달이 <b>통째로 새 요금</b>으로 나갔습니다. 올리면 쓰지도 않은
          날의 비싼 요금을 고객이 내고, 내리면 우리가 덜 받았습니다. 이제 쓴 날짜만큼만
          받습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '하루에 조건은 하나',
              desc: '같은 날 두 번 바꾸면 마지막에 정한 것이 그날의 조건입니다. 하루가 두 번 청구되지 않습니다.',
            },
            {
              term: '금액이 같으면 한 줄',
              desc: '바꿨다가 같은 날 되돌리면 날짜만 갈라지고 금액은 같으므로, 청구서에는 한 줄로 합쳐 나옵니다.',
            },
            {
              term: '이미 발행한 청구서는 그대로',
              desc: '지난 기간의 금액 근거는 바뀌지 않습니다. 지난 구간은 그때 요금제의 가격으로 남습니다.',
            },
            {
              term: '초과 사용료는 나누지 않습니다',
              desc: '이미 실제로 쓴 양이라 날짜 비율을 또 곱하면 쓴 것보다 적게 받게 됩니다.',
            },
            {
              term: '짧은 기간을 청구해도 한 달치를 받지 않습니다',
              desc: '나누는 기준은 고른 기간이 아니라 그 구독의 한 주기(보통 30~31일)입니다. 서비스 종료 정산처럼 5일만 청구할 때도 한 달치가 아니라 5/31 만 받습니다.',
            },
          ]}
        />

        <HelpH>할인 — 그 제품이 낼 돈을 넘지 않습니다</HelpH>
        <HelpP>
          할인은 <b>제품마다 따로</b> 걸립니다. CRM 에 건 할인은 CRM 이 낼 금액
          (기본료 + 수량 요금 + 초과 사용료)에서만 빠집니다. <b>그 금액을 넘으면 거기서
          멈춥니다</b> — 넘친 할인이 다른 제품 요금을 깎는 일은 없습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '비율 할인 (%)',
              desc: '0~100 사이의 %로 입력합니다(10 = 10%). 그 제품 소계에 비율을 곱합니다.',
            },
            {
              term: '정액 할인 (원)',
              desc: '원 단위로 입력합니다. 그 제품의 청구액보다 크면 청구액까지만 적용되고, 청구서 적요에 「남은 청구액까지만 적용 (요청 ○○원)」 이라고 적힙니다.',
            },
            {
              term: '둘을 함께 걸면',
              desc: '먼저 건 할인부터 차례로 적용하고, 합계가 그 제품 소계에 닿으면 멈춥니다. 목록의 「할인」 칸에 걸려 있는 것이 모두 보입니다.',
            },
            { term: '해제', desc: '같은 방식으로 0 을 넣으면 해제됩니다.' },
          ]}
        />
        <HelpP>
          <b>날짜만큼만 깎습니다.</b> 10월 26일에 10% 할인을 걸면 10월 청구서에서
          10월 한 달이 아니라 <b>26일~31일 6일치</b>만 할인됩니다. 중간에 끝나는 할인도
          같습니다 — 15일까지 유효했으면 그 15일치만 빠집니다. 청구서 적요에{' '}
          <HelpPath>2026-10-26~2026-10-31 6/31일</HelpPath> 처럼 적혀 고객이 검산할 수
          있습니다.
        </HelpP>
        <HelpP>
          이미 <b>발행한 청구서는 바뀌지 않습니다.</b> 아직 발행하지 않은 기간부터
          적용되고, 기간이 끝난 뒤에 건 할인은 그 기간에 소급되지 않습니다. 승인자와
          사유는 감사로그에 남습니다.
        </HelpP>

        <HelpH>자동 / 수동 — 기업마다 따로 정합니다</HelpH>
        <HelpP>
          <b>자동</b>이면 배치가 <b>매일 새벽 2시</b>에 돌면서, 청구 주기가 끝나기
          며칠 전인 기업의 청구서를 만들고 안내 메일까지 보냅니다. 기업마다 유료
          시작일이 달라 <b>발행일도 기업마다 다릅니다</b> — 10월 20일에 시작한
          기업은 매달 20일 즈음에 나갑니다. 설정하지 않은 기업은 자동입니다.
        </HelpP>
        <HelpP>
          「며칠 전」과 전체 켜고 끄기는 <HelpPath>설정 → 공통코드 → BILLING_POLICY</HelpPath>{' '}
          의 <HelpPath>AUTO_INVOICE_LEAD_DAYS</HelpPath> ·{' '}
          <HelpPath>AUTO_INVOICE_ENABLED</HelpPath> 입니다. 지금 걸린 값은{' '}
          <HelpPath>일괄 청구</HelpPath> 탭 맨 위에 적혀 있습니다.
        </HelpP>
        <HelpH>발행부터 결제까지</HelpH>
        <HelpList
          items={[
            { term: '새벽 2시 · 발행', desc: '주기 종료 며칠 전. 그날 발행 대상인 기업만 처리합니다.' },
            {
              term: '다음 날 새벽 5시 · 자동 결제',
              desc: '결제수단(빌링키)을 등록한 기업만. 발행 당일에 바로 긁지 않습니다 — 금액에 이의가 들어올 수 있는 시점이라 하루 뒤부터 걷습니다. 결제 대행사(PG) 연동 전에는 돌지 않고, 입금 확인 후 수기로 기록합니다.',
            },
            {
              term: '거절되면 — 다시 긁고, 알립니다',
              desc: '한도 초과·유효기간 만료로 거절되면 결제 건이 「실패」로 남고 다음 날 새벽에 다시 시도합니다. 거절 사유를 담아 기업 관리자에게 메일이 나갑니다 — 1·3·7번째 시도에만 보냅니다(매일 보내면 안 읽히는 메일이 됩니다). 카드를 바꾸지 않아도 계좌이체로 넣으시면 그대로 정리됩니다.',
            },
            {
              term: '발행일 + 납기일수 · 납기',
              desc: '기본 14일이고 기업마다 다르게 둘 수 있습니다. 계좌이체로 받는 기업의 기한입니다.',
            },
            { term: '매일 새벽 7시 · 연체 표시', desc: '납기가 지났는데 다 안 들어온 청구서를 연체로 바꿉니다.' },
            {
              term: '납기 + 15일 · 자동 정지',
              desc: '기준일과 최소 금액은 BILLING_POLICY 의 SUSPEND_AFTER_DAYS · SUSPEND_MIN_AMOUNT 입니다. 입금이 확인되면 자동으로 풀립니다(운영자가 직접 멈춘 기업은 풀리지 않습니다).',
            },
          ]}
        />
        <HelpP>
          청구서 목록의 <b>발행일</b> 칸에 <HelpPath>자동</HelpPath> /{' '}
          <HelpPath>수동</HelpPath> 이 함께 붙습니다 — 배치가 보낸 것인지 사람이 보낸
          것인지 목록에서 바로 보입니다. <b>이용 기간</b>은 쓴 기간이고,{' '}
          <b>발행일</b>은 보낸 날입니다.
        </HelpP>
        <HelpP>
          <b>수동</b>으로 두면 배치가 그 기업을 건드리지 않습니다. 금액을 사람이 확인한
          뒤 보내야 하는 곳, 담당자와 날을 맞춰야 하는 곳에 씁니다. 전에는 전체
          스위치 하나뿐이라 그런 곳이 한 곳만 있어도 <b>모든 기업</b>을 손으로
          내보내야 했습니다.
        </HelpP>
        <HelpP>
          <HelpPath>일괄 청구</HelpPath> 탭에서 한 줄씩 켜고 끕니다. 납기일도 기업마다
          둘 수 있어, 매달 같은 값을 다시 넣지 않아도 됩니다.
        </HelpP>

        <HelpH>일괄 청구 — 목록이 곧 장바구니입니다</HelpH>
        <HelpP>
          청구할 수 있는 기업이 <b>금액과 함께</b> 이미 올라와 있습니다. 담는 과정이
          없습니다 — <b>빼야 할 것만 체크를 끄고</b> 단추를 한 번 누릅니다. 금액은
          기업마다 미리 계산한 값이라 목록을 여는 데 시간이 조금 걸립니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '자동 기업도 함께 보입니다',
              desc: '언제 얼마가 나갈지 미리 확인하시라고 같은 목록에 둡니다. 발행은 체크한 것만 됩니다.',
            },
            {
              term: '「계산 못 함」',
              desc: '그 기간에 청구할 금액이 없거나(체험 중 등) 조건이 어긋난 것입니다. 사유가 뜨고, 체크할 수 없습니다.',
            },
            {
              term: '결과는 건별로',
              desc: '한 곳이 실패해도 나머지는 발행됩니다. 「이미 있음」은 그 기간 청구서가 이미 나가 있다는 뜻으로, 두 번 청구된 것이 아닙니다.',
            },
          ]}
        />

        <HelpH>환불 — 계좌로 보내지 않고 다음 청구서에서 깎습니다</HelpH>
        <HelpP>
          환불을 기록하면 그 금액은 <b>갚아야 할 돈</b>으로 남습니다. 다음 번 청구서가
          나갈 때 <b>부가세까지 더한 합계에서</b> 자동으로 빠지고, 청구서에{' '}
          <HelpPath>환불 차감 — (사유)</HelpPath> 한 줄로 적힙니다. 고객이 왜 금액이
          줄었는지 묻지 않아도 되게 하기 위해서입니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '공급가가 아니라 합계에서 뺍니다',
              desc: '환불은 지난 거래의 수정이지 이번 달 매출이 준 것이 아닙니다. 공급가에서 빼면 부가세가 10% 더 깎여 세금계산서와 어긋납니다.',
            },
            {
              term: '청구액보다 크면 나눠 갚습니다',
              desc: '남는 만큼은 그다음 청구서로 넘어갑니다. 청구 금액이 음수가 되지는 않습니다 — 음수 청구서는 보낼 수도, 세금계산서로 끊을 수도 없습니다.',
            },
            {
              term: '두 번 깎이지 않습니다',
              desc: '어느 청구서에서 얼마를 갚았는지 따로 기록합니다. 그 청구서를 무효화하면 그만큼 되돌아와 다음에 다시 깎입니다.',
            },
            {
              term: '추적',
              desc: '「환불」 탭에서 사유·승인자·원 결제와 함께 봅니다. 가장 중요한 칸은 「아직 못 갚음」이고, 줄을 펼치면 어느 청구서에서 얼마가 깎였는지 보입니다.',
            },
          ]}
        />

        <HelpH>계좌이체 입금은 여기서 기록합니다</HelpH>
        <HelpP>
          결제 대행사(PG) 연동 전이라 <b>계좌이체로 들어온 돈은 사람이 확인해
          기록</b>합니다. <HelpPath>청구서</HelpPath> 탭에서 그 청구서 줄의{' '}
          <b>「수납 기록」</b> 단추를 누르고, 들어온 금액·수단(계좌이체)·<b>입금자명</b>·
          <b>실제 입금일</b>을 넣으면 됩니다.
        </HelpP>
        <HelpP>
          <HelpPath>수납 내역</HelpPath> 화면에는 등록 단추가 <b>없습니다.</b> 어느
          청구서에 넣을지를 골라야 하므로 청구서 쪽에 있습니다. 적고 나면 그 내역이
          수납 내역 목록에 나타납니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '입금자명을 따로 받는 이유',
              desc: '통장에 찍힌 이름이 상호와 다른 일이 흔합니다(대표자 개인 명의, 모회사 명의). 그대로 적어 두어야 나중에 통장과 맞춰 볼 수 있습니다.',
            },
            {
              term: '실제 입금일이 매출의 달을 정합니다',
              desc: '월말에 들어온 돈을 다음 달에 기록하면서 오늘로 남기면 매출 월이 어긋납니다. 통장에 찍힌 날을 넣어 주세요.',
            },
            {
              term: '일부만 들어와도 기록합니다',
              desc: '원 단위가 어긋나 일부만 들어오는 일이 있습니다. 남은 금액은 그대로 미수금에 남고, 「부분수납」으로 보입니다.',
            },
          ]}
        />

        <HelpH>세금계산서 — 홈택스 엑셀 일괄 등록</HelpH>
        <HelpP>
          계좌이체로 받는 기업에는 세금계산서를 끊어 드려야 합니다.{' '}
          <HelpPath>세금계산서</HelpPath> 탭에서 청구서를 고르면{' '}
          <b>국세청 공식 양식</b> 그대로 담은 엑셀을 내려받습니다. 홈택스{' '}
          <b>「전자(세)계산서 → 엑셀 일괄 발급」</b> 에 그 파일을 올리시면 됩니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '세 단계입니다',
              desc: '① 양식 파일 받기 → ② 홈택스에 올려 발급 → ③ 홈택스에서 「매출 전자세금계산서 목록」을 txt 로 내려받아 올리면 승인번호가 한 번에 적힙니다. ②가 우리 손 밖이라 ①과 ③을 따로 표시합니다.',
            },
            {
              term: '진행 상태가 뜻하는 것',
              desc: '「발급 전」 = 아직 양식에 담지 않음 · 「양식 내려받음」 = 파일은 받았지만 홈택스에 아직 안 올림 · 「발급 완료」 = 승인번호까지 적힘 · 「기업 정보 부족」 = 그 기업의 사업자등록번호·대표자·주소·업태·종목·이메일 중 비어 있는 것이 있어 양식에 넣지 못하는 상태.',
            },
            {
              term: '승인번호 일괄 등록 — 무엇으로 맞추나',
              desc: '① 홈택스 「비고」에 남아 있는 우리 청구서 번호로 먼저 찾습니다(양식을 만들 때 그 칸에 적어 두고, 그대로 되돌아옵니다). ② 비고가 없으면 사업자등록번호·작성일자·공급가액·세액 네 가지가 모두 같은 청구서로 찾습니다. ③ 둘 다 아니면 「못 찾음」으로 둡니다 — 짐작으로 붙이지 않습니다. 세금계산서를 엉뚱한 거래에 매다는 것이 가장 나쁜 결과입니다.',
            },
            {
              term: '올리면 미리보기가 먼저 뜹니다',
              desc: '무엇이 어떻게 붙을지 보여 주고, 「적용」을 눌러야 저장됩니다. 「번호 충돌」은 그 청구서에 이미 다른 승인번호가 적혀 있다는 뜻이고 덮어쓰지 않습니다. 「모호함」은 같은 조건의 청구서가 둘 이상이라는 뜻입니다. 다른 회사의 목록 파일을 올리면 공급자 등록번호가 달라 거절합니다.',
            },
            {
              term: '한 파일에 100건까지',
              desc: '국세청 제한입니다. 더 고르면 거절하고 기간을 나눠 받으라고 알려 드립니다 — 조용히 잘라 보내면 빠진 건을 아무도 모릅니다.',
            },
            {
              term: '공급가액은 환불 차감 전 금액입니다',
              desc: '환불 차감은 받을 돈을 줄인 것이지 그달의 공급가액이 줄어든 것이 아닙니다. 공급가액에서 빼면 세금계산서가 실제 거래와 어긋납니다.',
            },
            {
              term: '영수 / 청구',
              desc: '돈을 이미 받았으면 영수(01), 아직이면 청구(02) 로 적고 현금·외상미수금 칸을 그에 맞춰 채웁니다. 국세청이 필수로 요구하는 칸입니다.',
            },
            {
              term: '「기업 정보 부족」이 뜨면',
              desc: '그 기업의 사업자등록번호·대표자·주소·업태·종목·수신 이메일 중 비어 있는 것이 있습니다. 그대로 올리면 국세청이 그 줄만 튕겨 내는데 100줄 중 어느 줄인지 찾기 어려워, 담기 전에 막습니다. 기업 정보를 채운 뒤 다시 시도하세요.',
            },
            {
              term: '「계산서 받을 메일」은 어디서 정하나',
              desc: '그 기업이 「구성원 → 청구 문서 수신」 으로 지정한 분들입니다. 지정이 없으면 그 기업의 소유자·관리자가 들어갑니다. 양식이 두 칸만 받으므로 앞의 두 분까지만 담기고, 세 번째부터는 목록에 「외 N명(제외)」 으로 표시됩니다.',
            },
            {
              term: '공급자(우리) 정보',
              desc: '탭 맨 위 카드에서 고칩니다. 사업자등록증에 적힌 그대로 넣어 주세요 — 발급되는 모든 계산서의 공급자 칸에 그대로 들어갑니다. 비어 있으면 양식을 만들 수 없습니다.',
            },
          ]}
        />

        <HelpH>기업 이름을 누르면 — 거래 원장</HelpH>
        <HelpP>
          어느 표에서든 <b>기업 이름을 누르면</b> 그 기업의 청구·수납·환불·차감이{' '}
          <b>시간순 한 줄기</b>로 열립니다. 표를 셋으로 나눠 보면 "이 입금이 어느
          청구서 것인가" 를 사람이 맞춰야 하는데, 한 줄기로 펴면 눈으로 따라 읽힙니다.
        </HelpP>
        <HelpP>
          오른쪽 <b>「받을 돈」</b>은 그 시점까지의 누계입니다. 청구는 더하고, 수납은
          빼고, 환불은 <b>돌려줘야 할 돈</b>이라 더하고, 다음 청구서에서 갚으면 뺍니다.
          마지막 줄의 값이 지금 그 기업에서 받을 돈입니다.
        </HelpP>

        <HelpH>청구서 상태</HelpH>
        <HelpList
          items={[
            { term: '발행', desc: '보냈고 납기가 아직 남았습니다.' },
            { term: '부분 수납', desc: '일부만 들어왔습니다. 남은 금액은 그대로 미수금에 잡히고, 자동 결제 대상에도 남습니다.' },
            { term: '연체', desc: '납기가 지났는데 다 안 들어왔습니다. 매일 새벽 7시에 배치가 표시를 바꿉니다.' },
            { term: '완납', desc: '다 들어왔습니다.' },
            { term: '무효', desc: '잘못 발행해 취소한 것입니다. 수납이 한 건이라도 있으면 환불을 먼저 해야 무효화됩니다.' },
          ]}
        />

        <HelpH>체험 기간</HelpH>
        <HelpP>
          구독을 만들 때 체험 종료일을 정합니다. 그날이 지나면 그 기업은 제품에서 조회만
          가능해지고 등록·수정이 막힙니다. 만료 7일·3일·1일 전에 자동으로 메일이 갑니다.
        </HelpP>
        <HelpH>다른 화면과의 연결</HelpH>
        <HelpP>
          요금제의 내용(가격·한도)은 <HelpPath>요금제</HelpPath> 에서 정합니다. 여기서는
          "어느 기업이 어느 요금제를 쓰는가" 만 정합니다.
        </HelpP>
      </>
    ),
  },

  plans: {
    title: '요금제 — 화면 설명',
    body: (
      <>
        <HelpP>
          제품마다 요금제를 만들고 가격과 한도를 정합니다. 여기 만든 것이 그대로 고객의
          <HelpPath>제품</HelpPath> 화면과 홍보 사이트 요금표에 나오고, 매달 청구서의
          계산 근거가 됩니다.
        </HelpP>

        <HelpH>청구액은 이렇게 나옵니다</HelpH>
        <HelpP>
          <b>기본료 + 단위당 금액 × (계약 수량 − 기본 포함 수량) + 초과 사용료 − 할인</b>
          <br />
          마지막에 부가세 10%가 붙습니다.
        </HelpP>
        <HelpP>
          예) 기본료 없음 · 사용자당 5,500원 · 포함 0명인 요금제를 <b>10명</b>으로 계약하면
          매달 <b>55,000원</b>입니다.
        </HelpP>

        <HelpH>달 중간에 시작하거나 끝나면 — 날짜로 나눠 받습니다</HelpH>
        <HelpP>
          기본료와 수량 요금은 <b>쓴 날짜만큼만</b> 청구합니다. 10월 25일에 가입한
          기업에게 10월 한 달치를 받지 않습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '받기 시작하는 날',
              desc: '가입일 · 무료 체험이 끝난 다음 날 · 청구 기간 시작일 중 가장 늦은 날입니다.',
            },
            { term: '받기를 끝내는 날', desc: '해지일과 청구 기간 끝 중 이른 날입니다.' },
            {
              term: '초과 사용료는 나누지 않습니다',
              desc: '저장 용량·트래픽 초과분은 이미 실제로 쓴 양이라, 거기에 날짜 비율을 또 곱하면 쓴 것보다 적게 받게 됩니다.',
            },
          ]}
        />
        <HelpP>
          예) 55,000원짜리를 10월 25일에 시작하면 55,000 × 7/31 = <b>12,419원</b>.
          청구서 적요에 <HelpPath>2026-10-25~2026-10-31 7/31일</HelpPath> 처럼 적혀 있어
          고객이 직접 검산할 수 있습니다.
        </HelpP>

        <HelpH>수량과 사용량은 다릅니다</HelpH>
        <HelpList
          items={[
            {
              term: '계약 수량',
              desc: '구독할 때 정해 둔 숫자(사용자 10명, 사업장 3곳). 실제로 몇 명이 쓰는지와 무관하게 이 숫자로 청구합니다. 바꾸려면 그 기업의 구독·청구 화면에서 수량을 고칩니다.',
            },
            {
              term: '실제 사용량',
              desc: '시스템이 날마다 집계하는 값(저장 용량, 트래픽, 실제 접속자 수). 아래 「사용량 한도」가 이 값을 봅니다.',
            },
          ]}
        />

        <HelpH>사용량 한도 — 세 칸만 보면 됩니다</HelpH>
        <HelpList
          items={[
            {
              term: '기본 제공',
              desc: '계약 수량과 상관없이 깔아 주는 몫입니다.',
            },
            {
              term: '수량당 추가',
              desc: (
                <>
                  계약 수량 1 단위마다 더해 주는 몫입니다. <b>실제 한도 = 기본 제공 +
                  수량당 추가 × 계약 수량</b>. 비워 두면 3명짜리 기업과 100명짜리 기업이
                  같은 용량을 받습니다 — 용량처럼 사람이 늘면 같이 늘어야 하는 것은 여기에
                  적습니다.
                </>
              ),
            },
            {
              term: '초과 단가',
              desc: (
                <>
                  고른 단위 <b>하나당 금액</b>입니다(GB 를 골랐으면 GB 당). <b>원 단위
                  정수만</b> 넣습니다 — 소수가 섞이면 청구서 합계와 부가세가 어긋납니다.
                  더 잘게 매기려면 금액을 쪼개지 말고 단위를 키우세요(GB당 0.5원 →
                  TB당 500원).
                </>
              ),
            },
          ]}
        />
        <HelpP>
          초과분은 <b>시작된 묶음을 올려서</b> 셉니다. 1GB 당 500원이면 1.2GB 를 넘겨 쓴
          경우 2GB 로 보아 1,000원입니다. 칸 아래에 「사용자 10이면 70GB」처럼 계산 결과가
          바로 뜨니 저장 전에 눈으로 확인하세요.
        </HelpP>
        <HelpP>
          <b>초과 허용을 끄면</b> 한도에 닿는 순간 막힙니다(추가 청구 없음). <b>켜면</b>
          계속 쓰되 넘은 만큼 청구합니다 — 단가를 반드시 넣어야 합니다.
        </HelpP>
        <HelpP>
          한 기업이 제품을 여럿 쓰더라도 <b>같은 항목의 초과료는 한 번만</b> 청구됩니다.
          저장 용량은 기업 단위로 하나이기 때문입니다. 요금제마다 한도가 다르면{' '}
          <b>모두 더합니다</b> — CRM 10명 + ECO 5명을 계약한 기업이 15명분을 내고도 10명에서
          막히면 안 되기 때문입니다. 단가가 다르면 <b>싼 쪽</b>을 씁니다.
        </HelpP>
        <HelpP>
          <b>기본 제공을 0 으로 두고 초과를 허용하면 종량제</b>가 됩니다. AI 보고서가
          그렇습니다 — 쓴 건수만큼만 청구되고 안 쓰면 0원이라, 기업에게 쓸지 말지를 묻지
          않고 모든 요금제에 넣어 두었습니다. 고객 화면에는 한도 막대 대신{' '}
          <b>「사용한 만큼」</b>으로 표시됩니다.
        </HelpP>
        <HelpP>
          항목마다 세는 방식이 다릅니다. <b>트래픽 · SMS</b>처럼 쌓이는 값은 한 달 합계로,
          <b> 저장 용량 · 사업장 수</b>처럼 현재 상태인 값은 그 시점의 값으로 봅니다. 줄
          아래 설명에 어느 쪽인지 적혀 있습니다.
        </HelpP>
        <HelpP>
          한도 항목에는 <b>그 제품이 실제로 올려 주는 값만</b> 나옵니다. 올라오지 않는 값에
          한도를 걸면 사용량이 0 으로 잡혀 영원히 과금되지 않습니다.
        </HelpP>
        {/* ── 목록 화면 ── */}
        <HelpH>목록에서 보이는 것</HelpH>
        <HelpList
          items={[
            {
              term: '상태 꼬리표',
              desc: '고객 요금표에 나오는지를 한 눈에 알려 줍니다. 마우스를 올리면 이유가 뜹니다.',
            },
            { term: '시행 기간', desc: '이 가격 버전이 언제부터 언제까지 적용되는지입니다.' },
            { term: '구독', desc: '이 요금제를 쓰고 있는 기업 수입니다. 0 이 아니면 가격을 바로 못 고칩니다.' },
          ]}
        />
        <HelpP>상태는 다섯 가지입니다.</HelpP>
        <HelpList
          items={[
            { term: '판매중', desc: '고객 요금표에 나옵니다.' },
            { term: '판매중지', desc: '신규 가입을 받지 않습니다. 이미 쓰는 기업은 그대로 씁니다.' },
            { term: '비공개', desc: '영업 협의 전용입니다. 요금표에 나오지 않습니다.' },
            { term: '시행 전', desc: '시행일이 아직 오지 않았습니다. 그날이 되면 저절로 나옵니다.' },
            { term: '종료됨', desc: '종료일이 지났습니다. 보통 새 가격 버전을 만들면 구버전이 이렇게 됩니다.' },
          ]}
        />
        <HelpP>
          요금표에 나오려면 <b>판매중 + 공개 + 시행일이 지났을 것</b> 세 가지를 모두
          만족해야 합니다. 하나라도 어긋나면 안 보입니다. 안 보이는 요금제를 찾을 때는
          조회 조건의 <b>「판매 중지된 요금제 포함」</b> 과 <b>「공개 여부」</b> 를 함께
          쓰시면 빨리 좁혀집니다.
        </HelpP>

        {/* ── 기본 정보 ── */}
        <HelpH>기본 정보</HelpH>
        <HelpList
          items={[
            { term: '판매 제품', desc: '어느 제품의 요금제인지. 만든 뒤에는 바꿀 수 없습니다. 아래의 포함 기능·한도 항목이 이 제품에 맞게 걸러집니다.' },
            { term: '요금제 코드', desc: '시스템이 쓰는 식별자입니다. 가격 버전이 달라도 코드는 같고, 청구서에 남습니다.' },
            { term: '표시 이름 · 설명', desc: '고객 요금표에 그대로 나옵니다. 설명에 금액을 글로 적지 마세요 — 가격을 바꿀 때 같이 고치지 않으면 틀린 금액이 남습니다.' },
            { term: '유형', desc: '무료·유료·협의 등 요금제 성격입니다. 화면 분류에 쓰입니다.' },
          ]}
        />

        {/* ── 가격 ── */}
        <HelpH>가격 — 과금 방식이 나머지를 결정합니다</HelpH>
        <HelpP>
          <b>과금 방식</b>을 고르면 아래 칸의 이름이 함께 바뀝니다. 「사용자당」을 고르면
          「사용자 1명당(원)」·「기본료에 포함된 사용자 수」처럼 바뀌어, 무엇의 수인지
          헷갈리지 않습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '정액을 고르면',
              desc: '수량 칸이 아예 사라집니다. 수량과 상관없이 기본료만 받습니다. ⚠ 정액으로 둔 채 단위당 금액을 적어 두면 그 금액은 청구서에 붙지 않습니다 — 사용자 수에 따라 더 받으려면 반드시 「사용자당」으로 바꾸세요.',
            },
            {
              term: '단위 표시명',
              desc: '청구서와 요금표에 쓸 말입니다. 비우면 과금 방식의 기본 이름(사용자·사업장)을 씁니다. 장비를 센다면 「장비」로 적으세요.',
            },
            {
              term: '기본료에 포함된 수',
              desc: '여기까지는 더 받지 않습니다. 포함 2 · 단위당 10,000원이면 3번째부터 1명당 10,000원이 붙습니다.',
            },
            { term: '최소 수', desc: '가입할 때의 최저 계약 수량입니다. 가입하면 이 값으로 시작합니다.' },
            { term: '최대 수', desc: '넘을 수 없는 상한입니다. 비우면 무제한입니다.' },
          ]}
        />
        <HelpP>
          칸 아래 초록 상자에 <b>지금 넣은 값으로 얼마가 청구되는지</b> 계산해 보여 줍니다.
          숫자를 바꿀 때마다 따라 움직이니, 저장 전에 금액을 눈으로 확인하세요.
        </HelpP>

        {/* ── 포함 기능 ── */}
        <HelpH>포함 기능</HelpH>
        <HelpP>
          고른 기능이 <b>고객 요금표의 체크 목록</b>에 그대로 나오고, 제품에서 그 화면을 열 수
          있는지를 결정합니다. 목록은 <b>이 제품의 기능</b>과 <b>공통 기능</b>으로 나뉘어
          있습니다.
        </HelpP>
        <HelpP>
          다른 제품의 기능은 고를 수 없습니다. 예전에 잘못 들어간 것이 있으면{' '}
          <b>「⚠ 다른 제품의 기능」</b> 묶음에 모아 보여 주니 빼 주세요 — 그 제품에는 해당
          화면이 없어 실제로 쓸 수 없는데 요금표에는 제공 기능으로 찍힙니다.
        </HelpP>
        <HelpP>
          기본 기능(로그인 · 프로필 · 공지)은 고르지 않아도 <b>항상 들어갑니다.</b>
        </HelpP>

        {/* ── 가격 변경 ── */}
        <HelpH>가격을 바꿀 때</HelpH>
        <HelpP>
          구독 중인 기업이 있으면 <b>가격 칸이 잠깁니다.</b> 그 자리에서 고치면 이미 보낸
          청구서의 근거가 달라지기 때문입니다. 대신 <b>「새 가격 버전」</b> 으로 시행일이
          다른 버전을 만듭니다. 지난 청구서는 그때 가격 그대로 남고, 시행일부터 새 가격이
          적용됩니다.
        </HelpP>
        <HelpP>
          잠기는 것은 금액 칸만이 아닙니다. <b>청구액을 바꾸는 값은 모두</b> 잠깁니다 —
          기본료 · 단위당 금액 · <b>기본료에 포함된 수</b> · <b>과금 방식</b> · 과금 주기 ·{' '}
          <b>초과 단가</b>. 포함 수를 0 에서 5 로 올리면 단가를 한 푼도 건드리지 않고
          모든 기업의 청구액이 5명분씩 줄어들고, 과금 방식을 「정액」으로 바꾸면 수량
          요금이 통째로 사라집니다. 쓰는 동안 단가가 바뀌면 고객은 몰랐던 금액으로
          청구받습니다.
        </HelpP>
        <HelpP>
          <b>한도(기본 제공·수량당 추가)는 잠기지 않습니다.</b> 한도를 늘려 주는 것은
          고객에게 유리한 쪽이고 자주 필요하기 때문입니다. 막히는 것은 금액인{' '}
          <b>초과 단가와 묶음</b>뿐입니다.
        </HelpP>

        <HelpH>저장할 때 막히는 것 — 조용히 0원이 되는 조합</HelpH>
        <HelpP>
          아래는 저장이 거절됩니다. 모두 <b>청구가 조용히 멈추는</b> 쪽이라, 몇 달 뒤
          매출이 비어 있는 것을 보고서야 알게 되는 종류입니다. 새로 만들 때뿐 아니라{' '}
          <b>고칠 때와 새 가격 버전을 만들 때도</b> 똑같이 검사합니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '초과 허용 켜고 단가 비움',
              desc: '한도를 넘겨 써도 0원이 청구됩니다. 막을 생각이면 「초과 허용」을 끄세요.',
            },
            {
              term: '사용자당인데 단가 0원',
              desc: '사람이 몇 명으로 늘어도 0원입니다. 정액으로 둘 생각이면 과금 방식을 「정액」으로 바꾸세요.',
            },
            {
              term: '무료 요금제에 초과 과금',
              desc: '0원 요금제를 쓰는 기업은 결제 수단이 없습니다. 받을 수 없는 돈이 미수금으로 쌓입니다.',
            },
            { term: '최대 수량 < 최소 수량', desc: '아무도 가입할 수 없는 요금제가 됩니다.' },
          ]}
        />
        <HelpP>
          주의할 점이 하나 있습니다. 새 버전의 시행일을 <b>미래로 잡으면 그날까지 공백이
          생길 수 있습니다.</b> 구버전을 판매중지하면 그 자리에서 종료되는데, 새 버전은
          아직 시행 전이라 둘 다 요금표에 안 나오기 때문입니다. 바로 바꾸실 거면 시행일을
          오늘로 두세요.
        </HelpP>

        <HelpH>요금제를 지우지 않는 이유</HelpH>
        <HelpP>
          지난 청구서가 그 요금제를 가리키고 있습니다. 지우면 "이 금액이 왜 나왔는지" 를
          설명할 수 없습니다. 그래서 삭제 대신 <b>판매중지</b>로 관리합니다.
        </HelpP>
      </>
    ),
  },

  codes: {
    title: '공통코드 — 화면 설명',
    body: (
      <>
        <HelpP>
          화면 곳곳의 선택 목록(예: 상태, 구분)을 여기서 관리합니다. 코드를 고치면 관련
          화면의 선택지가 바뀝니다.
        </HelpP>
        <HelpH>지우기 전에</HelpH>
        <HelpP>
          이미 그 코드를 쓰고 있는 자료가 있으면, 지우는 대신 <b>사용 안 함</b>으로 두세요.
          지우면 과거 자료가 무엇을 가리키던 것인지 알 수 없게 됩니다.
        </HelpP>
        <HelpH>제품이 받아 쓰는 코드</HelpH>
        <HelpP>
          일부 코드는 여기서 정하면 <b>제품이 그대로 따릅니다.</b>{' '}
          <b>LOG_RETENTION</b>(로그 보관 기간)이 그렇습니다 — WORK&CRM·WORK&ECO 가 매일
          이 값을 받아 그보다 오래된 기록을 지웁니다. 기업이 제품 화면에서 바꿀 수 없게
          막아 두었습니다. 법이 정한 최소 보관 기간보다 짧게 줄이면 저장되지 않습니다.
        </HelpP>

        <HelpH>시스템 코드와 기업이 더한 코드</HelpH>
        <HelpP>
          한 그룹 안에 두 종류가 섞여 있습니다. 목록의 「기업」 칸으로 가릅니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '시스템 코드',
              desc: '우리가 정한 값입니다. 여기서 고치고 지웁니다. 다만 is_system 인 것은 DB 제약과 짝이라 코드값을 바꿀 수 없고, 지우는 대신 비활성으로 둡니다.',
            },
            {
              term: '기업이 더한 코드',
              desc: '「기업 확장 허용」을 켠 그룹에 고객사가 직접 넣은 값입니다. 여기서는 고치거나 지울 수 없습니다 — 고객 자료를 말없이 건드리지 않기 위해서입니다. 고객사가 자기 화면에서 고치거나 지웁니다.',
            },
          ]}
        />
        <HelpP>
          그래서 <b>기업이 더한 항목이 하나라도 있으면 그 그룹은 지워지지 않습니다.</b>{' '}
          지워야 한다면 고객사에 그 항목을 먼저 지워 달라고 요청하세요. 급하면
          그룹을 <b>비활성</b>으로 두면 선택 목록에서 사라집니다.
        </HelpP>

        <HelpH>기업 확장 허용</HelpH>
        <HelpP>
          켜면 고객사가 그 그룹에 자기 값을 더할 수 있습니다(예: 직급, 문서 분류).
          끄면 우리가 정한 값만 씁니다. <b>상태·유형처럼 코드가 곧 동작을 가르는
          그룹은 켜지 마세요</b> — 고객이 넣은 값을 코드가 알아듣지 못합니다.
        </HelpP>
      </>
    ),
  },

  mail: {
    title: '메일 설정 — 화면 설명',
    body: (
      <>
        <HelpP>
          플랫폼이 보내는 메일(가입 인증코드, 초대, 체험 만료 안내)과{' '}
          <b>WORK&CRM·WORK&ECO 가 보내는 알림까지</b> 모두 이 설정으로 나갑니다. 제품마다
          따로 메일 서버를 두지 않습니다 — 예전에는 기업이 WORK&CRM 회사정보에 자기 SMTP 를
          넣어야 했고, 넣지 않은 기업은 알림이 아예 가지 않았습니다.
        </HelpP>

        <HelpH>맨 위 띠가 지금 상태입니다</HelpH>
        <HelpList
          items={[
            { term: '연결됨', desc: '설정이 다 찼고 마지막 확인도 성공했습니다.' },
            {
              term: '확인 필요',
              desc: '빠진 칸이 있거나 마지막 확인이 실패했습니다. 무엇이 모자란지 띠에 적혀 있습니다.',
            },
            { term: '보내지 않음', desc: '발송 경로가 「보내지 않는다」로 되어 있습니다.' },
          ]}
        />

        <HelpH>발송 경로 두 가지</HelpH>
        <HelpList
          items={[
            {
              term: '네이버 클라우드 SENS (권장)',
              desc: 'API 로 한 건씩 보냅니다. 사서함이 없어 보낸편지함에 쌓이지 않고 월 한도도 넉넉합니다. 서비스 ID·접근키·비밀키 세 가지가 필요합니다.',
            },
            {
              term: 'SMTP 서버',
              desc: 'Gmail 등 메일 계정으로 보냅니다. 보낸 메일이 그 계정에 쌓이고, Gmail 은 하루 약 500통 제한이 있습니다.',
            },
          ]}
        />

        <HelpH>SENS 를 쓰면 「연결 확인」이 잠깁니다</HelpH>
        <HelpP>
          <b>붙어 볼 연결이 없기 때문입니다.</b> SMTP 는 서버에 접속해 로그인까지 해 볼 수
          있지만, SENS 는 보낼 때마다 HTTPS 로 한 건씩 넘기는 방식이라 「로그인해 본다」가
          성립하지 않습니다. <b>시험 발송</b>으로 한 통 받아 보는 것이 유일한 확인
          방법이며, 그 결과가 「마지막 확인」에 적힙니다.
        </HelpP>

        <HelpH>설정이 맞아도 스팸으로 떨어질 수 있습니다</HelpH>
        <HelpP>
          발신 주소의 도메인에 <b>SPF·DKIM·DMARC</b> 레코드가 없으면 받는 쪽 메일 서버가
          위조로 봅니다. 네이버 클라우드 콘솔에서 발신 주소를 등록하고 DNS 레코드를 추가한
          뒤에 회사 도메인 주소로 바꾸세요. <b>인증 전에 발신 주소만 회사 도메인으로 바꾸면
          오히려 더 많이 차단됩니다.</b>
        </HelpP>

        <HelpH>재기동이 필요 없습니다</HelpH>
        <HelpP>
          여기서 저장한 값이 설정 파일(.env)보다 우선하고, 저장하는 즉시 다음 발송부터
          쓰입니다. 「설정 출처」에 지금 무엇이 적용 중인지 표시됩니다.
        </HelpP>

        <HelpH>「최근 발송」과 전체 로그의 「알림 발송」은 쓰임이 다릅니다</HelpH>
        <HelpP>
          같은 기록을 보지만 묻는 것이 다릅니다. 둘 다 <b>받는 사람 주소는 보여 주지
          않습니다</b> — 설정 화면 한 장이 연락처 명부가 되면 안 되기 때문입니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '여기 「최근 발송」',
              desc: '방금 고친 설정이 먹었는지 보는 자리입니다. 최근 10건만 보여 주는 대신, 발송처(네이버 클라우드)에 다시 물어 실제 결과까지 가져옵니다.',
            },
            {
              term: '전체 로그 > 알림 발송',
              desc: '지난 기록을 기간·채널·상태로 찾고 엑셀로 받는 자리입니다. 「지난달에 이 기업에 안내가 나갔나」를 확인할 때 씁니다.',
            },
          ]}
        />
        <HelpP>
          <b>「발송됨」은 보냈다는 뜻이 아닙니다.</b> 네이버 클라우드가 접수했다는 뜻이고,
          그 뒤에 조용히 실패할 수 있습니다(발신 주소 미등록, 받는 서버 거절). 그래서
          「최근 발송」이 실제 결과를 다시 물어봅니다 — 「메일이 안 왔다」는 문의는 여기서
          답합니다.
        </HelpP>

        <HelpH>주의</HelpH>
        <HelpP>
          <b>발신 주소는 반드시 이메일 형식</b>이어야 합니다. 회사 이름을 넣으면 저장되지
          않습니다. 메일이 나가지 않으면 가입 인증코드가 전달되지 않아 아무도 가입할 수
          없습니다.
        </HelpP>
      </>
    ),
  },

  ai: {
    title: 'AI 설정 — 화면 설명',
    body: (
      <>
        <HelpP>
          AI 보고서가 어느 모델로 돌아가는지를 정합니다. <b>API 키는 공급사 것이고 여기에만
          있습니다</b> — 제품(WORK&CRM·WORK&ECO)에 내려보내지 않습니다. 제품 서버가 뚫려도
          이 키로 남의 돈을 쓸 수 없게 하기 위해서입니다.
        </HelpP>

        <HelpH>엔진은 둘, 고르는 쪽은 기업</HelpH>
        <HelpP>
          Claude 와 Gemini 를 등록해 두면, 기업이{' '}
          <HelpPath>기업 설정 → AI 보고서</HelpPath> 에서 둘 중 하나를 고릅니다(기본값
          Claude). 여기서 <b>사용 끄기</b>로 두면 그 엔진을 고른 기업은 보고서를 만들 수
          없게 되니, 바꾸기 전에 쓰는 기업이 있는지 확인하세요.
        </HelpP>

        <HelpH>칸이 뜻하는 것</HelpH>
        <HelpList
          items={[
            {
              term: '모델',
              desc: '실제로 부를 모델 이름입니다. 오타가 나면 호출이 통째로 실패하므로 저장 뒤 「연결 확인」으로 한 번 불러 보세요.',
            },
            {
              term: '100만 토큰당 입력·출력 단가(USD)',
              desc: '원가 계산용입니다. 고객에게 받는 금액은 이 값이 아니라 요금제의 「AI 보고서 생성」 단가(건당)입니다. 단가가 오르면 여기를 고쳐 두어야 남는지 아닌지를 볼 수 있습니다.',
            },
            {
              term: '한 번에 보낼 수 있는 글자 수',
              desc: '제품이 이 값을 받아 분석 자료를 자릅니다. 넘치면 보고서를 만들기 전에 사용자에게 알려 줍니다.',
            },
          ]}
        />

        <HelpH>연결 확인</HelpH>
        <HelpP>
          짧은 질문을 실제로 한 번 보냅니다. <b>요금이 아주 조금 발생합니다</b>(몇 원
          수준). 키를 바꾼 뒤에는 꼭 눌러 보세요 — 틀린 키는 고객이 보고서를 만들 때야
          드러납니다.
        </HelpP>

        <HelpH>요금은 어디서 정하나</HelpH>
        <HelpP>
          고객에게 받는 금액은 <HelpPath>요금제</HelpPath> 의 사용량 한도 중{' '}
          <b>「AI 보고서 생성」</b> 로 시작하는 줄에서 정합니다. 기본 제공 건수 + 초과 허용 +
          건당 단가로 두면, 제공 건수까지는 공짜이고 그 뒤로는 <b>쓴 만큼만</b> 청구됩니다.
        </HelpP>
        <HelpP>
          <b>줄이 세 개인 것은 정상입니다.</b> 보고서를 만드는 자리가 셋이라 지표도 셋으로
          나뉩니다 — <b>AI 보고서 생성 (Hub 자사 현황)</b>,{' '}
          <b>AI 보고서 생성 (WORK&CRM)</b>, <b>AI 보고서 생성 (WORK&ECO)</b>. 하나로 합치면
          청구서에서 어느 제품이 썼는지 알 수 없고, 제품마다 단가를 달리할 수도 없습니다.
          한 요금제에 같은 이름이 두 줄 보인다면 그건 중복이 아니라 <b>Hub 몫 한 줄 +
          제품 몫 한 줄</b> 입니다.
        </HelpP>
        <HelpP>
          지금 값은 제품 몫이 <b>월 2건 제공 · 이후 건당 350원</b>, Hub 자사 현황 몫이{' '}
          <b>제공 없음 · 건당 350원</b> 입니다. Hub 몫에 제공량을 두지 않는 이유는, 그
          보고서가 제품을 구독한 기업에게 덤으로 열어 주는 기능이기 때문입니다 — 쓰지
          않으면 0원입니다.
        </HelpP>
        <HelpP>
          그래서 <b>요금제에 그 줄이 없으면 보고서를 만들 수 없습니다.</b> 고객이
          「AI 보고서를 못 쓴다」고 하면 엔진 설정보다 먼저 그 기업의 요금제에{' '}
          <b>제품.ai_report</b> 한도가 들어 있는지 보세요 — 구독하지 않은 제품으로는
          애초에 돌지 않습니다(돈이 새지 않게 막아 둔 것입니다).
        </HelpP>

        <HelpH>저장한 키는 다시 볼 수 없습니다</HelpH>
        <HelpP>
          목록에는 <b>키가 있는지 여부</b>만 나옵니다. 값 자체는 내려오지 않으니 화면을
          열어 확인할 수 없습니다. 키를 잃어버렸으면 공급사 콘솔에서 새로 발급해 다시
          저장하세요.
        </HelpP>
      </>
    ),
  },

  revenue: {
    title: '매출 현황 — 화면 설명',
    body: (
      <>
        <HelpP>
          숫자를 <b>보는</b> 화면입니다. 청구서 발행·수납 기록처럼 <b>일을 하는</b> 것은
          「구독·청구」에서 합니다.
        </HelpP>
        <HelpH>네 칸이 뜻하는 것</HelpH>
        <HelpList
          items={[
            {
              term: '확정 매출',
              desc: (
                <>
                  고른 기간에 <b>발행한</b> 청구서의 합입니다(부가세 포함). 회계가 보는
                  숫자입니다.
                </>
              ),
            },
            {
              term: '수납',
              desc: (
                <>
                  고른 기간에 <b>들어온</b> 돈입니다(입금일 기준). 자금이 보는 숫자입니다.
                  9월분을 10월에 받으면 확정 매출은 9월, 수납은 10월에 잡힙니다.
                  <HelpPath>구독·청구</HelpPath> 위쪽 「이번 달 수납」과 <b>같은 식</b>으로
                  셉니다 — 두 화면의 숫자가 다르면 어느 쪽도 믿을 수 없기 때문입니다.
                  <br />
                  <b>환불은 돌려준 달에서 뺍니다.</b> 10월에 받은 돈을 11월에 환불하면
                  10월 수납은 그대로이고 11월 수납이 그만큼 줄어듭니다. 이미 마감해
                  보고한 달의 숫자가 나중에 달라지면 안 되기 때문입니다.
                </>
              ),
            },
            {
              term: '수납률 (막대)',
              desc: (
                <>
                  <b>그 기간에 발행한 청구서</b>가 지금까지 얼마나 걷혔는가입니다. 위의
                  「수납 ÷ 확정 매출」이 아닙니다 — 둘은 모집단이 달라서, 지난달 청구서를
                  이번 달에 많이 받으면 100%를 넘어 버립니다.
                </>
              ),
            },
            {
              term: '예상 매출 (월)',
              desc: '지금 구독이 그대로 유지될 때 매달 들어올 금액입니다. 아직 청구하지 않은 신규 구독도 들어 있습니다. 체험 중인 구독은 빠지고, 체험이 유료로 바뀌면 더해질 금액은 괄호로 따로 적습니다.',
            },
            {
              term: '미수금',
              desc: (
                <>
                  <b>기간과 무관합니다.</b> 언제 발행했든 지금 안 들어온 돈 전부입니다. 조회
                  기간을 바꿔도 이 칸은 움직이지 않습니다. 부분 수납된 청구서는{' '}
                  <b>남은 만큼만</b> 셉니다.
                </>
              ),
            },
          ]}
        />
        <HelpH>제품별 매출 — 세 숫자를 나눠 봅니다</HelpH>
        <HelpP>
          「발행액」과 「월 예상」은 <b>다른 시간을 가리킵니다.</b> 섞어 보면 어느 쪽 숫자인지
          알 수 없어 따로 적습니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '발행액',
              desc: (
                <>
                  고른 기간에 그 제품으로 <b>실제로 청구한</b> 금액입니다. 이미 일어난
                  일입니다. 할인은 빠져 있고 <b>부가세는 들어 있지 않습니다</b>(공급가액) —
                  부가세는 제품에 속하지 않기 때문입니다. 그래서 위의 「확정 매출」보다
                  10% 작습니다.
                </>
              ),
            },
            {
              term: '월 예상',
              desc: '유료 구독이 그대로 유지될 때 그 제품에서 매달 들어올 금액입니다. 앞으로의 숫자입니다.',
            },
            {
              term: '체험 +',
              desc: '체험 중인 구독이 유료로 바뀌면 더해질 금액입니다. 아직 돈이 아니므로 「월 예상」에 넣지 않습니다.',
            },
          ]}
        />
        <HelpP>
          제품이 늘어도 이 표는 손댈 것이 없습니다. <HelpPath>제품</HelpPath> 에 등록하고{' '}
          <HelpPath>요금제</HelpPath> 를 만들면 그대로 한 줄이 생깁니다.
        </HelpP>
        <HelpH>월별 추이의 「발행 − 수납」</HelpH>
        <HelpP>
          그달 발행액에서 그달 수납액을 뺀 값입니다. <b>미수금이 아닙니다.</b> 발행과
          수납은 모집단이 다르므로, 지난달 청구서를 이번 달에 많이 받으면 음수(+ 표시)가
          됩니다. 실제로 못 받은 돈은 위의 「미수금」을 보세요.
        </HelpP>
        <HelpH>연체와 자동 정지</HelpH>
        <HelpP>
          납기를 넘긴 기업은 아래 「연체 기업」에 모입니다. 공통코드{' '}
          <b>BILLING_POLICY</b> 의 <b>SUSPEND_AFTER_DAYS</b>(기본 15일)를 넘기면 배치가 자동으로
          서비스를 멈추고 안내 메일을 보냅니다. <b>입금이 확인되면 자동으로 풀립니다.</b>
        </HelpP>
        <HelpP>
          잔돈은 정지 사유로 삼지 않습니다. 같은 공통코드의{' '}
          <b>SUSPEND_MIN_AMOUNT</b>(기본 1,000원)보다 적게 남은 기업은 멈추지 않습니다 —
          계좌이체는 끝자리가 어긋나는 일이 흔한데, 남은 1원 때문에 기업 전체가 멈추면 안
          되기 때문입니다.
        </HelpP>
        <HelpP>
          「자동 정지」와 「운영자 정지」를 구분해 표시합니다. 입금 시 자동으로 푸는 것은{' '}
          <b>자동 정지된 기업뿐</b>입니다 — 사유가 있어 직접 멈춰 둔 기업까지 같이 풀리면 안
          되기 때문입니다.
        </HelpP>
        <HelpH>엑셀로 내보내기</HelpH>
        <HelpP>
          표마다 내보내기 단추가 있습니다. 화면에 보이는 조건 그대로 받습니다. 금액은 쉼표 없이
          숫자로 들어가므로 엑셀에서 바로 합계를 낼 수 있습니다.
        </HelpP>
      </>
    ),
  },

  'mail-templates': {
    title: '메일 서식 — 화면 설명',
    body: (
      <>
        <HelpP>
          플랫폼이 보내는 글의 <b>제목과 본문</b>을 고칩니다. 저장하면 다음 발송부터 바로
          나갑니다. 배포를 기다릴 필요가 없습니다.
        </HelpP>
        <HelpH>고치기 전에 「발송」 칸을 보세요</HelpH>
        <HelpP>
          글마다 <b>무엇이 그 글을 내보내는지</b>가 적혀 있습니다. 고쳤을 때 누구에게 언제
          나가는지가 여기서 갈립니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '자동',
              desc: '시스템이 때가 되면 저절로 보냅니다. 고치면 다음 배치 때 해당하는 모든 기업에 그대로 나갑니다. 가장 조심할 글입니다.',
            },
            {
              term: '사람 동작',
              desc: '가입·초대·로그인처럼 누군가 그 동작을 했을 때만 나갑니다. 그 사람 한 명에게만 갑니다.',
            },
            {
              term: '운영자가 누를 때',
              desc: '운영자가 단추를 눌러야 나갑니다. 시험 발송이 여기에 속합니다.',
            },
            {
              term: '메일 아님',
              desc: '메일로 나가지 않고 화면에 띄우는 글입니다. 고쳐도 메일함에는 아무것도 가지 않습니다. 「서비스 종료 전 안내문」이 그렇습니다.',
            },
          ]}
        />
        <HelpH>「다음 발송」은 베껴 적은 값이 아닙니다</HelpH>
        <HelpP>
          <b>지금 돌고 있는 배치에서 읽어 온 시각</b>입니다. 그래서 배치 시각이 바뀌면 이
          화면도 따라 바뀌고, 어긋날 일이 없습니다. 수정 창을 열면 그 배치의 이름과 주기도
          함께 보여 줍니다.
        </HelpP>
        <HelpList
          items={[
            {
              term: '등록 안 됨',
              desc: '그 배치가 서버에 올라와 있지 않습니다. 이 글은 지금 저절로 나가지 않습니다. 개발 담당자에게 알려 주세요.',
            },
            {
              term: '확인되지 않음',
              desc: '새로 생긴 서식인데 언제 나가는 글인지 아직 등록되지 않았습니다. 빈칸으로 두지 않고 붉게 보여 줍니다.',
            },
          ]}
        />
        <HelpP>
          「며칠 전에 알릴지」처럼 <b>횟수와 시점을 정하는 값</b>은 서식이 아니라 공통코드에
          있습니다. 무료 기간 종료 예고는 <HelpPath>공통코드 → TRIAL_NOTICE</HelpPath>,
          자료 파기 예고는 <HelpPath>공통코드 → TERMINATION_POLICY</HelpPath>,
          미납 정지 기준은 <HelpPath>공통코드 → BILLING_POLICY</HelpPath> 입니다.
        </HelpP>

        <HelpH>중괄호는 값이 들어갈 자리입니다</HelpH>
        <HelpP>
          본문의 <b>{'{{이름}}'}</b> 은 보낼 때 실제 값으로 바뀝니다. 서식마다 쓸 수 있는 이름이
          정해져 있고 수정 창 위에 보여 줍니다. <b>없는 이름을 적으면 그 자리는 빈칸</b>이 되니
          목록에 있는 것만 쓰세요.
        </HelpP>
        <HelpP>
          반대로 <b>본문이 요구하는 자리를 보내는 쪽이 채우지 못하면 그 메일은 통째로 나가지
          않습니다.</b> 목록에 없는 이름을 새로 넣지 마세요.
        </HelpP>
        <HelpH>지울 수 없습니다</HelpH>
        <HelpList
          items={[
            { term: '서식 코드', desc: '보내는 쪽 프로그램이 가리키는 이름이라 바꿀 수 없습니다.' },
            {
              term: '사용 끄기',
              desc: '글이 안 나가는 것이 아니라 밋밋한 기본 문구로 나갑니다. 끄지 말고 내용을 고치세요.',
            },
          ]}
        />
        <HelpH>모든 기업에 나갑니다</HelpH>
        <HelpP>
          여기 있는 글은 공용입니다. 특정 기업에만 다르게 보내는 기능은 아직 쓰지 않습니다.
        </HelpP>

        <HelpH>메일 설정과 무엇이 다른가</HelpH>
        <HelpList
          items={[
            {
              term: '메일 설정',
              desc: '어떤 길로 보낼지를 정합니다(네이버 클라우드 SENS · SMTP, 발신 주소).',
            },
            {
              term: '메일 서식 (여기)',
              desc: '그 길로 보낼 글의 제목과 본문을 정합니다.',
            },
          ]}
        />
        <HelpP>
          서식을 아무리 고쳐도 <b>메일 설정이 「확인 필요」면 한 통도 나가지 않습니다.</b>{' '}
          글이 안 간다는 문의가 오면 서식보다 설정을 먼저 보세요.
        </HelpP>
        <HelpP>
          「이 메일이 왜 안 왔지」는 <b>세 가지를 차례로</b> 봅니다. ① 이 화면의 「발송」이
          자동인가(사람 동작이면 그 동작을 해야 나갑니다) ② 「다음 발송」이 등록돼 있는가
          ③ <HelpPath>메일 설정 → 최근 발송</HelpPath> 에 기록이 있는가. 기록이 있는데 안
          왔다면 <b>발송처에 확인</b> 단추로 네이버 클라우드의 실제 결과를 묻습니다.
        </HelpP>

        <HelpH>고친 글이 실제로 어떻게 나갔는지</HelpH>
        <HelpP>
          <HelpPath>메일 설정 → 최근 발송</HelpPath> 에서 방금 나간 것을, 지난 기록은{' '}
          <HelpPath>전체 로그 → 알림 발송</HelpPath> 에서 봅니다. 받는 사람 주소는 두
          곳 다 보여 주지 않습니다.
        </HelpP>
      </>
    ),
  },

  integrations: {
    title: '외부 연동 — 화면 설명',
    body: (
      <>
        <HelpP>
          가입·결제실패 같은 사건이 생겼을 때 그 사실을 <b>외부 시스템으로 내보냅니다.</b> 고객사
          ERP·그룹웨어·메신저가 받아 가도록 할 때 씁니다.
        </HelpP>
        <HelpH>두 방향을 구분하세요</HelpH>
        <HelpList
          items={[
            { term: '나가는 연동', desc: '이 화면에서 관리합니다. 우리가 상대를 부릅니다.' },
            {
              term: '들어오는 연동',
              desc: '상대가 우리를 부릅니다. 제품 현황의 서비스 키로 관리합니다.',
            },
          ]}
        />
        <HelpH>실패해도 업무는 멈추지 않습니다</HelpH>
        <HelpP>
          보낼 것을 줄에 세워 두고 1분마다 도는 배치가 내보냅니다. 상대 서버가 느리다고 가입이
          지연되거나 되돌아가지 않습니다.
        </HelpP>
        <HelpH>보낸 내용은 보이지 않습니다</HelpH>
        <HelpP>
          이름·메일이 섞이므로 암호화해 보관합니다. 이력에는 상대 응답과 실패 사유만 남습니다.
        </HelpP>

        <HelpH>등록할 때</HelpH>
        <HelpList
          items={[
            {
              term: '주소는 http 또는 https 만',
              desc: 'ftp·file 같은 것은 거절됩니다. 운영에서는 https 를 쓰세요 — 사건 내용이 평문으로 나갑니다.',
            },
            {
              term: '연동 코드',
              desc: '우리가 그 연동을 가리키는 이름입니다. 전송 이력과 재시도가 이 코드로 묶입니다.',
            },
            {
              term: '「테스트」로 먼저 한 번',
              desc: '실제 사건을 기다리지 말고 눌러 보세요. 상대 서버가 받는지, 어떤 응답을 주는지 바로 나옵니다.',
            },
          ]}
        />

        <HelpH>실패한 것을 다시 보내기</HelpH>
        <HelpP>
          전송 이력에서 실패한 줄을 골라 재시도합니다. <b>상대가 같은 사건을 두 번 받을 수
          있으므로</b>, 받는 쪽이 중복을 걸러 내는지 확인하고 누르세요. 응답이 안 와서
          실패로 적혔을 뿐 상대는 이미 처리했을 수 있습니다.
        </HelpP>
      </>
    ),
  },

  footer: {
    title: '바닥글 — 화면 설명',
    body: (
      <>
        <HelpP>
          모든 화면 아래에 찍히는 <b>우리(공급사) 사업자 정보</b>를 고칩니다. 고객사의
          회사 정보가 아닙니다 — 그쪽은 기업마다 자기 <HelpPath>기업 설정</HelpPath> 에서
          관리합니다.
        </HelpP>
        <HelpH>왜 꼭 채워야 하나</HelpH>
        <HelpP>
          유료 구독을 받는 서비스는 <b>상호·대표자·주소·전화·사업자등록번호·통신판매업
          신고번호</b>를 이용자가 쉽게 볼 수 있는 곳에 표시할 의무가 있습니다. 로그인
          화면을 포함한 모든 화면에 함께 나옵니다.
        </HelpP>
        <HelpH>비워 두면</HelpH>
        <HelpP>
          그 항목은 <b>줄째로 사라집니다.</b> 「팩스: 」 같은 빈 칸이 남지 않습니다.
          전부 비우면 바닥글 띠 자체가 그려지지 않습니다. 저장 전에 아래
          <HelpPath>미리보기</HelpPath> 로 실제 모습을 확인할 수 있습니다.
        </HelpP>
        <HelpP>
          비워 둔 칸이 있어도 <b>저장은 됩니다.</b> 한 칸만 고쳐도 나머지는 그대로
          남습니다.
        </HelpP>
        <HelpH>두 개의 주소 칸</HelpH>
        <HelpP>
          <HelpPath>개인정보처리방침 주소</HelpPath> 와 <HelpPath>이용약관 주소</HelpPath> 는{' '}
          <b>http:// 또는 https:// 로 시작</b>해야 합니다. 이 두 칸은 바닥글에서 누를 수
          있는 링크가 되고, 그 링크는 <b>로그인 화면에도</b> 뜹니다 — 그래서 웹 주소가
          아닌 것은 받지 않습니다.
        </HelpP>
        <HelpH>입금 계좌와 증빙 서류는 여기가 아닙니다</HelpH>
        <HelpP>
          고객사가 이용료를 넣을 <b>계좌</b>와 <b>사업자등록증·통장 사본</b>은{' '}
          <HelpPath>과금 → 세금계산서</HelpPath> 맨 위에서 관리합니다. 바닥글은 모든 화면에
          찍히는 <b>표시 의무</b> 정보이고, 그쪽은 <b>돈을 받기 위한</b> 정보라 보는 사람도
          쓰임도 다릅니다.
        </HelpP>
        <HelpH>언제 반영되나</HelpH>
        <HelpP>
          저장하면 곧바로 바뀝니다. 보는 사람 화면에는 최대 5분 뒤에 반영됩니다
          (바닥글은 짧게 기억해 둡니다). 예전에는 이 값들이 프로그램 안에 박혀 있어
          <b> 전화번호 한 자리를 고치는 데도 다시 배포</b>해야 했습니다.
        </HelpP>
        <HelpH>누가 고칠 수 있나</HelpH>
        <HelpP>
          <b>시스템 관리자만</b> 고칩니다. 기업 관리자에게는 이 메뉴가 보이지 않고,
          주소로 직접 들어와도 열리지 않습니다. 누가 언제 무엇을 바꿨는지는{' '}
          <HelpPath>전체 로그</HelpPath> 에 남습니다.
        </HelpP>
      </>
    ),
  },

  'tenant-usage': {
    title: '기업별 사용량 — 화면 설명',
    body: (
      <>
        <HelpP>
          고객사가 <b>무엇을 얼마나 쓰고 있는지</b>를 한 표로 봅니다. 찾는 질문은 하나입니다 —
          <b> 어느 고객이 한도에 가까운가.</b> 한도에 닿기 전에 미리 연락드리거나 요금제를
          올려 드리기 위한 화면입니다.
        </HelpP>
        <HelpH>한도는 계약에서 가져옵니다</HelpH>
        <HelpP>
          한도는 그 기업이 <b>구독한 요금제</b>에서 계산합니다 (기본 제공량 + 수량당 제공량 ×
          계약 수량, 기업별 예외가 있으면 그 값). 제품을 여럿 구독하면 <b>모두 더합니다.</b>{' '}
          청구서를 계산하는 식과 같은 식입니다 — 다르면 「막히지도 않았는데 초과 요금이
          나왔다」가 됩니다.
        </HelpP>
        <HelpP>
          예전에는 한도를 계측 캐시에서 읽었습니다. 그 표는 <b>사용량이 한 번이라도 움직였을
          때만</b> 행이 생기므로, 아직 파일을 안 올린 기업이나 이번 달에 내려받은 적이 없는
          기업은 요금제에 16GB 가 걸려 있는데도 「0B / 한도 없음」으로 떴습니다. 지금은
          그렇지 않습니다.
        </HelpP>
        <HelpH>숫자가 갱신되는 때가 다릅니다</HelpH>
        <HelpList
          items={[
            {
              term: '저장 용량 · 파일 수',
              desc: '파일을 올리고 지울 때 그 자리에서 더하고 뺍니다. 여기에 더해 매일 새벽 3시에 원장(파일 표)에서 전부 다시 세어 덮어씁니다 — 중간에 어긋난 것이 있으면 그때 맞춰집니다.',
            },
            {
              term: '전송량 · AI 보고서',
              desc: '쓰는 즉시 쌓입니다. 전송량은 매월 1일에 0 부터 다시 시작합니다.',
            },
            {
              term: '구성원',
              desc: '매일 새벽 3시에 계정 표에서 다시 셉니다. 오늘 새로 초대한 사람은 내일 반영됩니다.',
            },
            {
              term: 'DB 사용량',
              desc: 'WORK&CRM · WORK&ECO 가 자기 데이터베이스 크기를 보고한 값을 더한 것입니다. 파일 저장 용량과는 별개로 셉니다.',
            },
          ]}
        />
        <HelpH>한도 초과</HelpH>
        <HelpP>
          빨간 딱지가 뜬 기업은 그 지표가 계약 한도를 넘었다는 뜻입니다. 넘었다고 해서 바로
          막히지는 않습니다 — 요금제가 초과를 허용하면 <b>초과분이 다음 청구서에 더해지고</b>,
          허용하지 않으면 그 기능이 막힙니다. 어느 쪽인지는{' '}
          <HelpPath>과금 → 요금제</HelpPath> 에서 보실 수 있습니다.
        </HelpP>
        <HelpH>파일 수는 왜 따로 있나</HelpH>
        <HelpP>
          <b>용량과 같이 읽어야 뜻이 생깁니다.</b> 같은 1GB 라도 큰 파일 열 개와 작은 파일
          십만 개는 백업·이관·삭제에 드는 품이 전혀 다릅니다. 한도는 용량에 걸리지만,{' '}
          <b>일이 얼마나 되는지</b>는 개수를 봐야 압니다.
        </HelpP>

        <HelpH>쪽 넘김과 검색</HelpH>
        <HelpP>
          표 아래에 <b>「전체 N곳 중 a–b」</b> 가 늘 적힙니다 — 걸러낸 결과를 전체로
          오해하지 않기 위해서입니다. 한 쪽에 몇 줄을 볼지는 오른쪽에서 고르실 수 있고,
          검색어를 넣거나 정렬을 바꾸면 <b>첫 쪽으로 돌아갑니다</b>(안 그러면 보던 쪽이
          비어 보입니다).
        </HelpP>
        <HelpP>
          <HelpPath>운영 현황</HelpPath> 맨 아래에도 같은 표가 있지만 그쪽은{' '}
          <b>상위 10곳만</b> 보여 줍니다. 검색·정렬·합계를 쓰시려면 이 화면입니다.
        </HelpP>
      </>
    ),
  },

  system: {
    title: '시스템 상태 — 화면 설명',
    body: (
      <>
        <HelpP>지금 서버가 괜찮은지 한눈에 봅니다. 수치를 읽기만 하고 아무것도 바꾸지 않습니다.</HelpP>
        <HelpH>어디를 잰 값인가</HelpH>
        <HelpList
          items={[
            { term: 'CPU · 메모리', desc: '서버(호스트) 전체 값입니다.' },
            {
              term: '디스크',
              desc: '「서버 디스크」는 호스트, 「첨부 저장소」는 추가로 붙인 디스크입니다.',
            },
            {
              term: '네트워크',
              desc: '표시된 꼬리표가 「API 컨테이너」면 그 컨테이너만의 값입니다. 서버 전체 값을 보려면 설정이 하나 더 필요합니다.',
            },
          ]}
        />
        <HelpH>무엇을 보고 놀라야 하나</HelpH>
        <HelpList
          items={[
            { term: '디스크 90% 이상', desc: '가장 급합니다. 차면 첨부 업로드와 DB 쓰기가 멈춥니다.' },
            { term: '이벤트 루프 지연', desc: '수십 ms 를 넘으면 무언가 오래 붙잡고 있는 것입니다.' },
            { term: '연결 수', desc: '최대치에 가까우면 새 요청이 대기합니다.' },
          ]}
        />
        <HelpH>서비스 점검이 말하는 것</HelpH>
        <HelpList
          items={[
            { term: '데이터베이스 · Redis', desc: '실제로 한 번 물어보고 응답 시간을 잽니다.' },
            {
              term: '메일 발송 경로',
              desc: (
                <>
                  지금 고른 경로(네이버 클라우드 또는 SMTP)에 <b>필요한 칸이 다 찼는지</b>만
                  봅니다. 실제로 도착하는지는 <HelpPath>메일 설정</HelpPath> 의 연결 확인·
                  시험 발송이 답합니다 — 설정이 멀쩡해도 발신 도메인 인증이 없으면 받는
                  쪽에서 버립니다.
                </>
              ),
            },
            { term: '첨부 저장소', desc: '쓸 수 있는 공간이 남아 있는지 봅니다.' },
          ]}
        />
        <HelpH>데이터베이스 — 막대와 미터 읽는 법</HelpH>
        <HelpList
          items={[
            {
              term: '큰 표의 막대',
              desc: '가장 큰 표를 100%로 본 상대 크기입니다. 숫자만 있으면 "5MB 와 1.4MB 중 어느 쪽이 문제인가" 가 한눈에 안 들어옵니다. 막대에 커서를 올리면 정확한 비율이 나옵니다.',
            },
            {
              term: '행 수는 추정치입니다',
              desc: '통계에서 읽은 값입니다. 정확히 세려면 표마다 전체를 훑어야 해서 화면을 열 때마다 그러지 않습니다.',
            },
            {
              term: '연결 미터',
              desc: '쓰고 있는 연결 / 최대치입니다. 80% 를 넘으면 「주의」, 90% 를 넘으면 「위험」으로 바뀝니다. 다 차면 새 요청이 줄을 서다 타임아웃으로 떨어지므로, 색이 바뀌면 그때 손써야 합니다.',
            },
          ]}
        />

        <HelpH>기업별 사용량은 여기 없습니다</HelpH>
        <HelpP>
          예전에는 이 화면 맨 아래에 기업 목록이 또 있었습니다. 지금은{' '}
          <HelpPath>고객 → 기업별 사용량</HelpPath> 한 곳에서만 봅니다 — 거기가 제품별·지표별로
          더 자세하고, <b>같은 숫자를 두 화면이 각자 그리면 한쪽만 고쳐지는 날</b>이 옵니다.
          이 화면은 서버 상태만 봅니다.
        </HelpP>
      </>
    ),
  },

  'admin-logs': {
    title: '전체 로그 — 화면 설명',
    body: (
      <>
        <HelpP>
          모든 기업의 기록을 봅니다. 기업 관리자의 <HelpPath>로그 조회</HelpPath> 와 같은
          내용이되 범위가 플랫폼 전체입니다.
        </HelpP>

        <HelpH>기업 화면과 왜 따로 있나</HelpH>
        <HelpP>
          범위를 <b>주소로</b> 가릅니다. 한 화면에서 「전체 보기」를 고르게 하면, 기업
          관리자가 그 값을 바꿔 보는 일이 생깁니다. 기업 화면은 자기 기업만, 이 화면은
          전체만 봅니다 — 섞이지 않습니다.
        </HelpP>
        <HelpP>
          그래서 <b>탭이 하나 더 많습니다.</b> 기업은 7종, 여기는 8종이고 추가된 것이{' '}
          <b>외부 연동 전송</b>입니다. 그것은 우리가 외부로 보내는 기록이라 특정 기업의
          것이 아닙니다.
        </HelpP>

        <HelpH>여덟 가지 원본</HelpH>
        <HelpList
          items={[
            { term: '감사로그', desc: '누가 무엇을 등록·수정·삭제·조회했나. 가장 많이 봅니다.' },
            { term: '로그인 시도', desc: '성공과 실패, 실패 사유. 계정 공격을 볼 때.' },
            { term: '세션', desc: '언제 들어와 언제 끊겼나, 아직 살아 있나.' },
            { term: '알림 발송', desc: '메일·알림이 나갔나. 받는 사람 주소는 싣지 않습니다.' },
            { term: '알람', desc: '임계치를 넘겨 자동으로 울린 것.' },
            { term: '파일 발급', desc: '첨부를 내려받거나 올릴 주소를 발급한 기록.' },
            { term: '설정 변경', desc: '공통코드·정책처럼 동작을 바꾸는 값이 언제 어떻게 바뀌었나.' },
            { term: '외부 연동 전송', desc: '이 화면에만 있습니다. 상대 응답과 실패 사유.' },
          ]}
        />

        <HelpH>보는 것도 기록에 남습니다</HelpH>
        <HelpP>
          감사로그를 누가 언제 들여다봤는지도 감사 대상입니다. 이 화면을 연 것, 엑셀로
          받은 것 모두 남습니다 — 나중에 「누가 고객 기록을 열람했나」에 답할 수 있어야
          하기 때문입니다.
        </HelpP>

        <HelpH>엑셀로 받기</HelpH>
        <HelpP>
          조회와 <b>다른 권한</b>을 요구합니다. 한 쪽씩 보는 것과 전 기업 기록을 통째로
          들고 나가는 것은 다른 일입니다. 상한에 걸려 잘리면 응답 머리말에 표시되고,
          화면이 「일부만 받았습니다」라고 알려 줍니다.
        </HelpP>

        <HelpH>보관 기간과 정리</HelpH>
        <HelpP>
          원본마다 보관 기간이 다르고, 값은 공통코드{' '}
          <HelpPath>LOG_RETENTION</HelpPath> 에서 정합니다. 이 화면에서는 지금 적용 중인
          기간과 남아 있는 양만 봅니다. 정리는 <b>매일 새벽 3시 30분</b>에 자동으로
          돌고, 「지금 정리」로 앞당길 수 있습니다.
        </HelpP>
        <HelpP>
          <b>법이 정한 최소 기간보다 짧게 줄일 수 없습니다.</b> 감사로그는 365일이
          하한이고, 그보다 작게 저장하려 하면 거절됩니다.
        </HelpP>

        <HelpH>지원 모드로 들어간 기록</HelpH>
        <HelpP>
          운영자가 고객사 화면을 들여다본 일도 여기에 남습니다. 지원 세션으로{' '}
          <b>고친</b> 것까지 그 세션 번호와 함께 남으므로, 고객사가 요청하면 「누가 언제
          무엇을 보고 고쳤는지」를 그대로 보여 줄 수 있습니다.
        </HelpP>
      </>
    ),
  },
};

/** 주소에서 현재 화면 키를 뽑는다. `/app/users/123` → `users` */
export function guideKeyFromPath(pathname: string): string | null {
  const m = /^\/app\/([^/]+)/.exec(pathname);
  return m ? (m[1] ?? null) : null;
}
